
import { useState, useEffect, useRef, type ReactNode, type CSSProperties, useCallback, useMemo } from "react";
import { showAlert } from '@/components/AppDialog';
import { useNavigate, Link } from "react-router-dom";
import OnboardingTour, { type TourStep } from "@/components/OnboardingTour";
import { getCapitalReport } from "@/data/capitalReports";
import { useReportContent } from "@/data/reportContent";
import { useNews } from "@/data/news";
import { useAutoNews, useBlogPosts, slugify, timeAgo, type BlogPost } from "@/data/blog";
import { setUnreadCount, computeUnread } from "@/utils/unread";
import { BRAND_SERVICE_VIEW } from "@/brand";
import { requestPath } from "@/config/serviceRequests";
import { ChevronLeft, ChevronRight, AlertCircle, Clock, FileText, Shield, MessageSquare, Zap, BarChart3, Target, Calendar, Sparkles, Download, Heart, Building2, ReceiptText, Lock, Crown, ArrowRight, ExternalLink } from "lucide-react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faInfo } from "@fortawesome/free-solid-svg-icons";
import Sidebar from "../components/Sidebar";
// Brand accent (blue-and-yellow on connectcorporate.com, blue on conncct.com). The
// `.btn-acc` / `.acc-*` classes in styles/appAccent.css are the same tokens for
// the places painted with Tailwind utilities rather than inline styles.
import { ACCENT, ACCENT_SOFT, ACCENT_ON_SOFT } from "../components/AppShell";
import MobileNav from "../components/MobileNav";
import UserProfileMenu from "../components/UserProfileMenu";
import Logo from "../components/Logo";
import { useAuth } from "../hooks/useAuth";
import { useTranslation } from "react-i18next";
import { storage } from "../firebase";
import { ref as storageRef, listAll } from "firebase/storage";
import { ensureOperateOwner } from "@/utils/operateStorage";

const cam = "https://storage.googleapis.com/connct-cba18.firebasestorage.app/frontend-assets/dashboad_comp/cam.png";
const doc = "https://storage.googleapis.com/connct-cba18.firebasestorage.app/frontend-assets/dashboad_comp/docs.png";
const leg = "https://storage.googleapis.com/connct-cba18.firebasestorage.app/frontend-assets/dashboad_comp/tailoredon stage.png";
const raise = "https://storage.googleapis.com/connct-cba18.firebasestorage.app/frontend-assets/dashboad_comp/raise cap.png";
const speak = "https://storage.googleapis.com/connct-cba18.firebasestorage.app/frontend-assets/dashboad_comp/money-insert_svgrepo.com.png";
const cloud = "https://storage.googleapis.com/connct-cba18.firebasestorage.app/frontend-assets/cloud.png";
import { API_BASE_URL } from '@/utils/env';

type UserData = {
    firstName?: string;
    lastName?: string;
    email?: string;
    phone?: string;
    goalAssessment?: any;
    investorReadinessForm1?: any;
    investorReadinessForm2?: any;
    irScore?: {
        totalScore: number;
        category: string;
        status: string;
        riskLevel: number;
        breakdown: any;
        calculatedAt: string;
    };
    onboardingCompleted?: boolean;
    capitalAssessment?: {
        stage?: string;
        raiseAmount?: string;
        fundingType?: string;
        fundraisingStage?: string;
        challenge?: string;
        completedAt?: string;
    };
    businessSetupData?: {
        type?: 'incorporate' | 'operate';
        companyName?: string;
        planName?: string;
        emirate?: string;
        total?: number;
        submittedAt?: string;
    };
    notifications?: Record<string, any>;
    capitalReadiness?: {
        score: number;
        raw: number;
        band: string;
        dilFlag?: boolean;
        submittedAt?: string;
    };
    purchasedPackage?: {
        name: string;
        flags: number[];
        band: string;
        purchasedAt: string;
    };
    purchasedPackages?: Record<string, { name: string; flags: number[]; band: string; purchasedAt: string }> | Array<{ name: string; flags: number[]; band: string; purchasedAt: string }>;
    consultingActivated?: boolean;
};

const Card = ({ children, className = "", style, "data-tour": tour }: { children: ReactNode; className?: string; style?: CSSProperties; "data-tour"?: string }) => {
    const hasBg = /\bbg-[^\s]+/.test(className);
    const baseBg = hasBg ? "" : "bg-white";
    return (
        <div data-tour={tour} className={`rounded-2xl border border-slate-200 ${baseBg} shadow-sm ${className}`} style={style}>
            {children}
        </div>
    );
};

const GaugeRing = ({ value, color, label }: { value: number; color: string; label: string }) => {
    const clamped = Math.min(Math.max(value, 0), 100);
    const circumference = 2 * Math.PI * 36;
    const dashOffset = circumference - (clamped / 100) * circumference;
    return (
        <div className="relative h-24 w-24 shrink-0">
            <svg className="h-24 w-24 -rotate-90" viewBox="0 0 80 80">
                <circle cx="40" cy="40" r="36" fill="none" stroke="#e5e7eb" strokeWidth="8" />
                <circle cx="40" cy="40" r="36" fill="none" stroke={color} strokeWidth="8"
                    strokeDasharray={circumference} strokeDashoffset={dashOffset} strokeLinecap="round" />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center text-sm font-semibold text-slate-900">
                {label}
            </div>
        </div>
    );
};

/* News & Advisory card.

   Reading happens INSIDE the app whenever it can: our own published articles
   (blog/current, written in /admin/blog) render at /articles/:slug, so they take
   the slot first and "Read Article" is an in-app navigation.

   Fallbacks, in order, so the card is never stale: the admin-curated news/current
   feed, then the hourly industry feed (news/auto). Both of those are third-party
   link-outs — we don't republish other publishers' bodies — so before linking out
   we check whether we've written our own piece about that headline (a draft made
   from a headline keeps the publisher in `sourceUrl`) and route to it instead.
   When only the external link exists the button says so rather than pretending
   the article opens here.

   useNews() returns a placeholder rather than an empty list, so that placeholder
   must lose to any real content — hence the explicit id check. */
type Spot = {
  title: string;
  category?: string;
  image?: string;
  author?: string;
  meta?: string;
  to?: string;    // in-app article route
  href?: string;  // external publisher (link-out only)
  source?: string;
};

const spotFromPost = (p: BlogPost): Spot => ({
  title: p.title,
  category: p.category,
  image: p.image,
  author: p.author,
  meta: p.readTime || timeAgo(p.publishedAt),
  to: `/articles/${p.slug}`,
});

function NewsSpotlight() {
  const adminNews = useNews();
  const auto = useAutoNews();
  const { posts } = useBlogPosts();

  const adminTop = adminNews[0];
  const hasAdmin = !!adminTop && adminTop.id !== 'placeholder-1';
  const autoTop = auto[0];

  // Have we written our own article about this headline? Match on the publisher
  // link first (drafts carry it), then on the title's slug.
  const ours = (title?: string, url?: string) => {
    const s = title ? slugify(title) : '';
    return posts.find(p => (url && p.sourceUrl === url) || (s && p.slug === s));
  };

  let n: Spot | null = null;
  if (posts[0]) {
    n = spotFromPost(posts[0]);
  } else if (hasAdmin) {
    const mine = ours(adminTop.title, adminTop.url);
    n = mine ? spotFromPost(mine) : {
      title: adminTop.title, category: adminTop.category, image: adminTop.image,
      author: adminTop.author, meta: adminTop.readTime,
      ...(adminTop.url ? { href: adminTop.url, source: adminTop.author } : { to: '/articles' }),
    };
  } else if (autoTop) {
    const mine = ours(autoTop.title, autoTop.url);
    n = mine ? spotFromPost(mine) : {
      title: autoTop.title, category: autoTop.tag, image: autoTop.image,
      author: autoTop.source, meta: timeAgo(autoTop.publishedAt),
      href: autoTop.url, source: autoTop.source,
    };
  } else if (adminTop) {
    // Nothing published anywhere yet — the placeholder, pointing at the (empty) list.
    n = {
      title: adminTop.title, category: adminTop.category, image: adminTop.image,
      author: adminTop.author, meta: adminTop.readTime, to: '/articles',
    };
  }
  if (!n) return null;

  const ctaCls = "flex items-center justify-center gap-1.5 text-center w-full py-2.5 rounded-xl text-sm font-bold text-[#17263A] transition hover:-translate-y-0.5 active:translate-y-0";
  const ctaStyle = { background: 'linear-gradient(135deg,#FFD54F,#E6B400)', boxShadow: '0 4px 14px rgba(230,180,0,.3)' };

  const media = (
    <>
      <img src={n.image || leg} alt={n.title} loading="lazy" onError={(e) => { (e.currentTarget as HTMLImageElement).src = leg; }} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
      <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg,rgba(10,24,38,0) 35%,rgba(10,24,38,.55) 78%,rgba(10,24,38,.82) 100%)' }} />
      {n.category && <span className="absolute top-3 left-3 text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full bg-amber-400 text-[#17263A] shadow-sm">{n.category}</span>}
      <p className="absolute bottom-3 left-3 right-3 text-[15px] font-bold text-white leading-snug" style={{ textShadow: '0 1px 8px rgba(0,0,0,.4)' }}>{n.title}</p>
    </>
  );

  return (
    <div data-tour="news" className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden group">
      <div className="px-5 pt-5 pb-3">
        <div className="flex items-center gap-2">
          <div className="w-1.5 h-5 rounded-full bg-amber-400" />
          <h3 className="text-sm font-bold text-gray-900">News &amp; Advisory</h3>
          <Link to="/articles" className="ms-auto text-[11px] font-bold text-gray-400 hover:text-gray-700">All articles →</Link>
        </div>
      </div>
      {/* The cover is the same click target as the button. */}
      {n.to
        ? <Link to={n.to} className="block relative mx-3 h-48 rounded-xl overflow-hidden">{media}</Link>
        : <a href={n.href} target="_blank" rel="noreferrer" className="block relative mx-3 h-48 rounded-xl overflow-hidden">{media}</a>}
      <div className="p-5 pt-4">
        <div className="flex items-center gap-2 mb-4 text-xs text-gray-400 font-medium">
          <span className="w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-bold text-white shrink-0" style={{ background: 'linear-gradient(135deg,#FFD54F,#E6B400)' }}>
            {(n.author || 'Conncct').trim().charAt(0).toUpperCase()}
          </span>
          <span className="text-gray-600 font-semibold">{n.author || 'Conncct Advisory'}</span>
          {n.meta && <><span className="text-gray-300">•</span><span>{n.meta}</span></>}
        </div>
        {n.to
          ? <Link to={n.to} className={ctaCls} style={ctaStyle}>Read Article →</Link>
          : <a href={n.href} target="_blank" rel="noreferrer" className={ctaCls} style={ctaStyle}>
              Read at {n.source || 'the source'} <ExternalLink className="w-3.5 h-3.5" />
            </a>}
      </div>
    </div>
  );
}

export default function Dashboard() {
    const navigate = useNavigate();
    const { t } = useTranslation();
    useReportContent(); // load admin report-text overrides so the capital report + PDF reflect edits
    const { user: authUser, loading: authLoading, logout } = useAuth();
    // Clear stale operate-onboarding flags left by a different account on this browser,
    // so the "Action Required" banner shows correctly for the current operate user.
    ensureOperateOwner();
    const [userData, setUserData] = useState<UserData | null>(null);
    // Keep the sidebar's unread-notification badge in sync whenever the record loads.
    useEffect(() => { if (userData) setUnreadCount(computeUnread((userData as any).notifications)); }, [userData]);
    const [loading, setLoading] = useState(true);
    // null until Storage answers. The upload prompt keys off this, and rendering it
    // on an assumption would flash a "you still need to upload" card at someone who
    // already has, so "unknown" is a state of its own rather than a default of false.
    const [hasUploadedDocs, setHasUploadedDocs] = useState<boolean | null>(null);
    const [showWelcome, setShowWelcome] = useState(false);

    // ── Guided tour ────────────────────────────────────────────────────────
    // Runs once per account on first arrival at the dashboard (i.e. straight
    // after sign-up). Completion is remembered per-uid in localStorage and
    // mirrored onto the user record, so it doesn't reappear on another device.
    // Append ?tour=1 to replay it at any time.
    const [showTour, setShowTour] = useState(false);
    const tourKey = authUser?.uid ? `tourDone_v2_${authUser.uid}` : null;

    useEffect(() => {
        if (loading || !authUser?.uid || !userData) return;
        const replay = new URLSearchParams(window.location.search).get('tour') === '1';
        if (replay) { setShowTour(true); return; }
        const seenLocally = tourKey ? localStorage.getItem(tourKey) === '1' : false;
        if (seenLocally || (userData as any).onboardingTourDoneV2) return;
        // Let the dashboard finish painting before measuring anything.
        const t = setTimeout(() => setShowTour(true), 700);
        return () => clearTimeout(t);
    }, [loading, authUser?.uid, userData, tourKey]);

    const finishTour = useCallback((completed: boolean, reason?: 'no-anchors') => {
        setShowTour(false);
        // Nothing was on screen to point at — leave the flags alone so the tour
        // gets another chance next visit instead of being lost silently.
        if (reason === 'no-anchors') return;
        if (tourKey) { try { localStorage.setItem(tourKey, '1'); } catch { /* private mode */ } }
        // Best-effort mirror so a different browser doesn't replay the tour.
        const uid = authUser?.uid;
        if (!uid) return;
        try {
            const tokensStr = localStorage.getItem('authTokens');
            const idToken = tokensStr ? JSON.parse(tokensStr).idToken : null;
            fetch(`${API_BASE_URL}/auth/user/${uid}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json', ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}) },
                body: JSON.stringify({ onboardingTourDoneV2: true, onboardingTourCompleted: completed }),
            }).catch(() => {});
        } catch { /* non-blocking */ }
    }, [tourKey, authUser?.uid]);

    /**
     * Tour steps. Anchors that aren't on screen are dropped by OnboardingTour
     * before it starts, so one list covers BOTH dashboards — the accounting view
     * and the consulting view each match the subset that exists for them.
     * Order = the order a new client should meet things in.
     */
    const tourSteps: TourStep[] = useMemo(() => [
        { selector: '[data-tour="get-started"]', title: 'Your setup checklist',
          body: 'The fastest route through onboarding. Each item unlocks more of the platform, and the bar tracks how far along you are. Start at the top and work down.' },
        { selector: '[data-tour="goal"]', title: 'Goal assessment',
          body: 'Tells us what you are raising, at what stage, and from whom. It sets the benchmark everything else is scored against, so it is worth doing first.' },
        { selector: '[data-tour="ir-score"]', title: 'Investor Readiness score',
          body: 'How an investor would grade you today across 14 factors: financials, traction, team and deal. Improve the weak factors and the score moves.' },
        { selector: '[data-tour="capital"]', title: 'Capital readiness',
          body: 'Your readiness to actually go out and raise, with the report behind it. This is the number our advisors work from when they prepare you.' },
        { selector: '[data-tour="profile"]', title: 'Profile completion',
          body: 'The gaps here are what investors ask for first. Filling them raises your score and saves a round of back-and-forth later.' },
        { selector: '[data-tour="services"]', title: 'Your active services',
          body: 'Everything you have with us (accounting, tax, company setup or advisory) with its current status, so you always know what is in progress.' },
        { selector: '[data-tour="nav:/company-profile"]', title: 'Company profile',
          body: 'Tell us about the business once. It feeds your reports, your score, and anything our advisors prepare on your behalf.' },
        { selector: '[data-tour="nav:/data-room"]', title: 'Data room',
          body: 'Financials, legal and pitch documents in one private place, organised so it is ready the moment an investor asks, not the week after.' },
        { selector: '[data-tour="nav:/accounting/data-room"]', title: 'Data room',
          body: 'Tax certificates, filings and your trade licence in one private place, organised and ready ahead of every FTA deadline.' },
        { selector: '[data-tour="nav:/financial-health-check"]', title: 'Financial health',
          body: 'A quick read on runway, cash and revenue. Your figures are saved, so you can come back and update them each month to watch the trend.' },
        { selector: '[data-tour="nav:/articles"]', title: 'Articles',
          body: 'Guidance written by the Conncct team: UAE tax and compliance, or getting investor-ready and raising, depending on the service you are on.' },
        { selector: '[data-tour="notifications"]', title: 'Notifications',
          body: 'Deadlines, document requests and anything needing your attention lands here. Worth a glance whenever you sign in.' },
        { selector: '[data-tour="news"]', title: 'News & advisory',
          body: 'The latest headline for your market, refreshed automatically, and a way through to the full article library.' },
    ], []);

    const [showScoreSheet, setShowScoreSheet] = useState(false);
    const [activeServiceView, setActiveServiceView] = useState<'accounting' | 'consulting'>(() => {
        // Brand split: each domain is single-product, so the dashboard view is pinned
        // to the domain's brand (corporate → accounting, consulting → consulting).
        return BRAND_SERVICE_VIEW;
    });
    const [serviceDropdownOpen, setServiceDropdownOpen] = useState(false);
    const [showConsultingUpsell, setShowConsultingUpsell] = useState(false);
    const [showAccountingUpsell, setShowAccountingUpsell] = useState(false);
    const [reportBusy, setReportBusy] = useState(false);
    const serviceDropdownRef = useRef<HTMLDivElement>(null);
    // Brand split: each domain is single-product, so the accounting/consulting switcher is hidden.
    const SHOW_SERVICE_TOGGLE = false;
    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (serviceDropdownRef.current && !serviceDropdownRef.current.contains(e.target as Node)) {
                setServiceDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);
    // Persist the active service view so the Sidebar/MobileNav show the matching
    // menu (accounting vs consulting), and notify them to update live.
    useEffect(() => {
        localStorage.setItem('activeServiceView', activeServiceView);
        window.dispatchEvent(new Event('serviceViewChanged'));
    }, [activeServiceView]);

    // Detect whether the user has any data-room files (Storage is the source of truth).
    // A prefix only exists under dataRoom/<uid> when at least one file has been uploaded.
    useEffect(() => {
        const uid = authUser?.uid;
        if (!uid) return;
        let cancelled = false;
        (async () => {
            try {
                const res = await listAll(storageRef(storage, `dataRoom/${uid}`));
                if (!cancelled) setHasUploadedDocs(res.prefixes.length > 0 || res.items.length > 0);
            } catch {
                // No folder yet, or Storage refused the listing. Either way, fall back to
                // "nothing uploaded" so the prompt still appears: hiding it on an error
                // would leave someone with no route to the data room.
                if (!cancelled) setHasUploadedDocs(false);
            }
        })();
        return () => { cancelled = true; };
    }, [authUser?.uid]);

    // Record that the user has activated consulting (so it unlocks in the data room and
    // elsewhere). Does NOT navigate — callers decide whether to open the consulting onboarding
    // (/readiness-journey) or just switch the view.
    const markConsultingActivated = () => {
        // Activating consulting makes it the active product, so the dashboard should land
        // on the consulting view. Persisting it here covers the whole consulting onboarding
        // chain (CapitalAssessment → Capital Readiness), whose exits navigate back to
        // /dashboard without otherwise setting the view — so an accounting user who finishes
        // (or skips) consulting onboarding lands on the consulting dashboard, not accounting.
        localStorage.setItem('activeServiceView', 'consulting');
        setActiveServiceView('consulting');
        window.dispatchEvent(new Event('serviceViewChanged'));
        if (userData?.consultingActivated) return;
        setUserData(prev => prev ? { ...prev, consultingActivated: true } : prev);
        const uid = authUser?.uid;
        if (!uid) return;
        const tokensStr = localStorage.getItem('authTokens');
        const idToken = tokensStr ? JSON.parse(tokensStr).idToken : null;
        fetch(`${API_BASE_URL}/auth/user/${uid}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json', ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}) },
            body: JSON.stringify({ consultingActivated: true }),
        }).catch(() => {});
    };

    // Activate accounting (mirrors markConsultingActivated): record `accountingActivated`
    // on the backend so the data room + dashboard treat accounting as active, switch the
    // view, and open the operate/incorporate chooser.
    const markAccountingActivated = () => {
        localStorage.setItem('activeServiceView', 'accounting');
        window.dispatchEvent(new Event('serviceViewChanged'));
        setShowAccountingUpsell(false);
        const uid = authUser?.uid;
        if (uid) {
            const tokensStr = localStorage.getItem('authTokens');
            const idToken = tokensStr ? JSON.parse(tokensStr).idToken : null;
            fetch(`${API_BASE_URL}/auth/user/${uid}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json', ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}) },
                body: JSON.stringify({ accountingActivated: true }),
            }).catch(() => {});
        }
        navigate('/accounting/select');
    };

    const [calendarOffset, setCalendarOffset] = useState(0);

    const groupInfo: Record<string, { title: string; description: string; color: string; progress: number }> = {
        'G1': { title: t('dashboard.capitalReadiness'), description: t('dashboard.capitalReadinessDesc'), color: '#FACC15', progress: 0.62 },
        'G2': { title: t('dashboard.growthArchitecture'), description: t('dashboard.growthArchitectureDesc'), color: '#FB923C', progress: 0.55 },
        'G3': { title: t('dashboard.growthFinancial'), description: t('dashboard.growthFinancialDesc'), color: '#3B82F6', progress: 0.70 },
        'G4': { title: t('dashboard.governance'), description: t('dashboard.governanceDesc'), color: '#8B5CF6', progress: 0.48 }
    };

    useEffect(() => {
        if (!authLoading && !authUser) {
            const userStr = localStorage.getItem('user');
            if (!userStr) { navigate('/login'); return; }
        }
        if (authLoading || !authUser) return;
        const fetchUserData = async () => {
            try {
                const hasSeenWelcome = sessionStorage.getItem('hasSeenWelcome');
                if (!hasSeenWelcome) {
                    setShowWelcome(true);
                    sessionStorage.setItem('hasSeenWelcome', 'true');
                    setTimeout(() => setShowWelcome(false), 5000);
                }
                const tokensStr = localStorage.getItem('authTokens');
                const idToken = tokensStr ? JSON.parse(tokensStr).idToken : null;
                const resp = await fetch(`${API_BASE_URL}/auth/user/${authUser.uid}`, {
                    headers: { ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}) }
                });
                if (resp.ok) {
                    const json = await resp.json();
                    if (json.success) {
                        setUserData(json.user);
                        // Auto-route setup/growth users to accounting view, but
                        // respect an explicit consulting choice (e.g. a user who was
                        // on accounting, activated consulting and just finished its
                        // onboarding should land on the consulting view, not accounting).
                        const setupType = json.user?.businessSetupData?.type;
                        if (setupType === 'operate' || setupType === 'incorporate') {
                            localStorage.setItem('setupService', setupType);
                            const explicitView = localStorage.getItem('activeServiceView');
                            if (explicitView !== 'consulting') {
                                setActiveServiceView('accounting');
                            }
                        }
                    }
                }
            } catch (error) {
                console.error('Error fetching user data:', error);
            } finally {
                setLoading(false);
            }
        };
        fetchUserData();
    }, [navigate, authUser, authLoading]);

    const emailPrefix = userData?.email ? userData.email.split('@')[0] : null;
    const userName = userData?.firstName && userData?.lastName
        ? `${userData.firstName} ${userData.lastName}`
        : emailPrefix || t('dashboard.userFallback');
    const onboardingComplete = userData?.onboardingCompleted || false;
    const hasGoalAssessment = !!userData?.goalAssessment?.group;
    const goalGroup = userData?.goalAssessment?.group || 'G1';
    const goalInfo = groupInfo[goalGroup];
    const hasIRScore = userData?.irScore?.totalScore !== undefined && userData?.irScore?.totalScore !== null;
    const irScore = userData?.irScore?.totalScore || 0;
    const irProgress = irScore / 100;
    // IR readiness band — same scale as Capital Readiness, so the ring colour and
    // description track the actual score (a high score is no longer painted "critical").
    const irColor = !hasIRScore ? '#9CA3AF'
      : irScore < 33 ? '#C0392B' : irScore < 41 ? '#E0592A' : irScore < 48 ? '#E08A1E'
      : irScore < 56 ? '#E6B400' : irScore < 65 ? '#1976D2' : irScore < 74 ? '#0E8F6E' : '#15824E';
    const irBandLabel = irScore < 33 ? 'Critical Risk' : irScore < 41 ? 'Very High Risk' : irScore < 48 ? 'High Risk'
      : irScore < 56 ? 'Medium Risk' : irScore < 65 ? 'Semi-Ready' : irScore < 74 ? 'Investor-Ready' : 'Exceptional';
    const irDesc = irScore < 41 ? 'Major foundational gaps remain: build your core investor assets.'
      : irScore < 56 ? 'Good progress. Sharpen your materials, metrics and diligence prep.'
      : irScore < 65 ? 'Nearly investor-ready. Refine your story and prepare to raise.'
      : 'Investor-ready. Engage investors with a strong, defensible story.';

    const hasBusinessSetup = !!userData?.businessSetupData?.submittedAt;
    // Accounting is "activated" once the user has gone through (or completed) accounting
    // setup — mirrors how consultingActivated works. A consulting-only user has none of
    // these, so accounting correctly shows as not activated for them.
    // Backend is the single source of truth — accounting is activated only when the
    // user's ORIGIN was accounting (flag set at verification / AccountingSelect) or they
    // explicitly activated it. No localStorage fallback, so a consulting-origin user
    // always sees accounting as deactivated (and can activate it).
    const hasAccountingActivated = !!(
        (userData as any)?.accountingActivated
        || userData?.businessSetupData?.type
        || userData?.businessSetupData?.submittedAt
    );
    const hasCrScore = !!userData?.capitalReadiness?.score;
    const crScore = userData?.capitalReadiness?.score ?? 0;
    const crBand  = userData?.capitalReadiness?.band ?? '';
    const crPct = Math.min(100, crScore);
    // Report is a paid unlock (demo purchase for now — Stripe later). The breakdown
    // and downloadable report are gated behind this flag.
    const reportUnlocked = !!(userData as any)?.capitalReportUnlocked;

    // Consulting profile completion — driven by the three readiness steps shown on the
    // dashboard (Goal Assessment, IR Score, Capital Assessment).
    const consultingSteps = [
        { key: 'goal', label: 'Goal Assessment', done: hasGoalAssessment },
        { key: 'ir', label: 'IR Score', done: hasIRScore },
        { key: 'capital', label: 'Capital Assessment', done: hasCrScore },
    ];
    const profileCompletion = Math.round((consultingSteps.filter(s => s.done).length / consultingSteps.length) * 100);

    // Open the user's Capital Readiness report in a new tab (view online + download).
    const handleDownloadReport = async () => {
        // Paywall: the report can only be viewed once unlocked (purchased).
        if (!reportUnlocked) { navigate('/capital-assessment'); return; }
        const report = getCapitalReport(goalGroup, crScore);
        if (!report) { navigate('/capital-assessment'); return; }
        // Open the tab synchronously (inside the click) so it isn't popup-blocked.
        const win = window.open('', '_blank');
        try {
            setReportBusy(true);
            const { openCapitalReportPdf } = await import('@/utils/capitalReportPdf');
            await openCapitalReportPdf(report, {
                userName,
                score: crScore,
                dateLabel: new Date().toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' }),
            }, win);
        } catch (err) {
            console.error('Failed to generate report PDF:', err);
            if (win && !win.closed) win.close();
            showAlert('Sorry, we could not generate your report. Please try again.');
        } finally {
            setReportBusy(false);
        }
    };

    // Real readiness score derived from user data
    const readinessScore = hasIRScore
        ? irScore
        : hasGoalAssessment
            ? (userData?.investorReadinessForm2 ? 55 : userData?.investorReadinessForm1 ? 35 : 18)
            : 0;

    const readinessMessage = readinessScore === 0
        ? 'Complete your goal assessment to begin tracking readiness'
        : readinessScore < 40
        ? 'Complete your investor readiness forms to improve your score'
        : readinessScore < 70
        ? 'Complete your legal audit to reach 90% score'
        : readinessScore < 90
        ? "You're almost there, fine-tune your profile to maximise"
        : "Excellent! You're fully investor-ready";

    const readinessColor = readinessScore < 40 ? '#EF4444' : readinessScore < 70 ? '#F59E0B' : '#10B981';

    function mapToR(score: number, t: any) {
        if (score <= 33) return { code: 'R1', label: t('dashboard.risk.critical') };
        if (score <= 41) return { code: 'R2', label: t('dashboard.risk.veryHigh') };
        if (score <= 48) return { code: 'R3', label: t('dashboard.risk.high') };
        if (score <= 56) return { code: 'R4', label: t('dashboard.risk.medium') };
        if (score <= 65) return { code: 'R5', label: t('dashboard.risk.semiReady') };
        if (score <= 74) return { code: 'R6', label: t('dashboard.risk.investorReady') };
        return { code: 'R7', label: t('dashboard.risk.exceptional') };
    }
    function getTitleForGR(g: string | undefined | null, rCode: string) {
        const gNum = g?.replace('G', '') || '1';
        const columns: Record<string, string> = { '1': 'capital', '2': 'mvp', '3': 'marketGrowth', '4': 'governance' };
        const rows: Record<string, string> = { 'R1': 'dreamer', 'R2': 'explorer', 'R3': 'builder', 'R4': 'shaper', 'R5': 'challenger', 'R6': 'trailblazer', 'R7': 'visionary' };
        return { column: columns[gNum] || 'foundation', row: rows[rCode] || 'dreamer' };
    }
    const rInfo = mapToR(irScore, t);
    const titleInfo = getTitleForGR(goalGroup, rInfo.code);

    // Calendar: always anchored to real today, offset shifts months
    const calToday = new Date();
    const calDisplayDate = new Date(calToday.getFullYear(), calToday.getMonth() + calendarOffset, 1);
    const calLabel = `${calDisplayDate.toLocaleString('default', { month: 'long' })} ${calDisplayDate.getFullYear()}`;

    // ── Compliance calendar data ──────────────────────────────────────────────
    // Prefer the client's REAL admin-set deadlines (adminOps.acc.deadlines, curated
    // in the Command Center). Fall back to generic recurring events only when the
    // team hasn't set any yet, so the calendar is never empty for a new client.
    const CAL_TYPE_COLOR: Record<string, string> = {
        vat: '#3B82F6', 'corporate tax': '#8B5CF6', ct: '#8B5CF6', tax: '#8B5CF6',
        licence: '#E69500', license: '#E69500', esr: '#0EA5E9', ubo: '#0EA5E9',
        payroll: '#EF4444', recon: '#10B981', close: '#F59E0B', audit: '#EC4899',
    };
    const calColorForType = (t?: string) => CAL_TYPE_COLOR[(t || '').toLowerCase().trim()] || '#1976D2';
    const accDeadlines: { name: string; date: string; type?: string }[] = (() => {
        const dl = (userData as any)?.adminOps?.acc?.deadlines;
        return Array.isArray(dl) ? dl.filter((d: any) => d && d.date && d.name) : [];
    })();
    const usingRealCal = accDeadlines.length > 0;
    const docRequests = Number((userData as any)?.adminOps?.acc?.docRequests || 0);
    const realCalEvents = accDeadlines.map(d => ({ dateObj: new Date(d.date), label: d.name, dot: calColorForType(d.type), type: d.type || '' }));

    // Month-grid dots for the displayed month
    const calComplianceEvents = (() => {
        const yr = calDisplayDate.getFullYear();
        const mo = calDisplayDate.getMonth();
        const lastDay = new Date(yr, mo + 1, 0).getDate();
        const events: Record<number, { label: string; dot: string }[]> = {};
        const addEv = (d: number, ev: { label: string; dot: string }) => {
            if (d >= 1 && d <= lastDay) { events[d] = [...(events[d] || []), ev]; }
        };
        if (usingRealCal) {
            realCalEvents.forEach(ev => {
                if (ev.dateObj.getFullYear() === yr && ev.dateObj.getMonth() === mo) addEv(ev.dateObj.getDate(), { label: ev.label, dot: ev.dot });
            });
            return events;
        }
        // Fallback: generic recurring events
        let lastFri = lastDay;
        while (new Date(yr, mo, lastFri).getDay() !== 5) lastFri--;
        const isQtrEnd = [2, 5, 8, 11].includes(mo);
        addEv(7,  { label: 'Payroll', dot: '#EF4444' });
        addEv(15, { label: 'Recon',   dot: '#10B981' });
        addEv(lastFri, { label: 'Close', dot: '#F59E0B' });
        if (isQtrEnd) addEv(lastDay, { label: 'VAT',  dot: '#3B82F6' });
        return events;
    })();

    // Full month grid starting on Monday
    const calendarGrid = (() => {
        const yr = calDisplayDate.getFullYear();
        const mo = calDisplayDate.getMonth();
        const firstDow = new Date(yr, mo, 1).getDay(); // 0=Sun
        const startOffset = firstDow === 0 ? 6 : firstDow - 1;
        const daysInMonth = new Date(yr, mo + 1, 0).getDate();
        const totalCells = Math.ceil((startOffset + daysInMonth) / 7) * 7;
        const cells = [];
        for (let i = 0; i < totalCells; i++) {
            const d = new Date(yr, mo, 1 - startOffset + i);
            const isToday = d.getFullYear() === calToday.getFullYear() && d.getMonth() === calToday.getMonth() && d.getDate() === calToday.getDate();
            cells.push({ date: d.getDate(), month: d.getMonth(), isCurrentMonth: d.getMonth() === mo, isToday, events: d.getMonth() === mo ? (calComplianceEvents[d.getDate()] || []) : [] });
        }
        return cells;
    })();

    // Upcoming filings from today — the client's real deadlines if set, else generic.
    const upcomingEvents = (() => {
        const startToday = new Date(calToday.getFullYear(), calToday.getMonth(), calToday.getDate());
        let evs: { dateObj: Date; label: string; dot: string }[] = [];
        if (usingRealCal) {
            evs = realCalEvents.filter(e => e.dateObj >= startToday).map(e => ({ dateObj: e.dateObj, label: e.label, dot: e.dot }));
        } else {
            for (let mOff = 0; mOff < 3; mOff++) {
                const yr = calToday.getFullYear();
                const mo = calToday.getMonth() + mOff;
                const lastDay = new Date(yr, mo + 1, 0).getDate();
                let lastFri = lastDay;
                while (new Date(yr, mo, lastFri).getDay() !== 5) lastFri--;
                const isQtrEnd = [2, 5, 8, 11].includes((mo + 12) % 12);
                const candidates = [
                    { day: 7,       label: 'Payroll Run',          dot: '#EF4444' },
                    { day: 15,      label: 'Bank Reconciliation',   dot: '#10B981' },
                    { day: lastFri, label: 'Month Close',           dot: '#F59E0B' },
                    ...(isQtrEnd ? [{ day: lastDay, label: 'VAT Filing', dot: '#3B82F6' }] : []),
                ];
                for (const c of candidates) {
                    const d = new Date(yr, mo, c.day);
                    if (d >= startToday) evs.push({ dateObj: d, label: c.label, dot: c.dot });
                }
            }
        }
        evs.sort((a, b) => a.dateObj.getTime() - b.dateObj.getTime());
        return evs.slice(0, usingRealCal ? 5 : 3).map(ev => ({
            ...ev,
            dateLabel: ev.dateObj.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
            isThisWeek: (ev.dateObj.getTime() - startToday.getTime()) < 7 * 24 * 60 * 60 * 1000,
        }));
    })();

    const pkgsRaw = userData?.purchasedPackages;
    const allPkgs = pkgsRaw
        ? (Array.isArray(pkgsRaw) ? pkgsRaw : Object.values(pkgsRaw)).filter(p => p && p.name)
        : (userData?.purchasedPackage?.name ? [userData.purchasedPackage] : []);

    // Accounting setup checklist state (drives the Compliance Status panel)
    const hasActiveService = allPkgs.length > 0;
    const setupSteps = [hasBusinessSetup, hasUploadedDocs, hasActiveService];
    const setupDoneCount = setupSteps.filter(Boolean).length;
    const setupFrac = setupDoneCount / setupSteps.length;
    const setupPct = Math.round(setupFrac * 100);
    const setupAllDone = setupDoneCount === setupSteps.length;

    // Business-setup order tracker — the client's order + the admin-set orderStatus.
    const orderStatusRaw = String((userData as any)?.orderStatus || 'new');
    const ORDER_STEPS = ['Received', 'In progress', 'Awaiting docs', 'Delivered'];
    const orderStepIdx = ({ new: 0, in_progress: 1, awaiting_docs: 2, delivered: 3 } as Record<string, number>)[orderStatusRaw] ?? 0;
    const orderCancelled = orderStatusRaw === 'cancelled';
    const bsd = userData?.businessSetupData;

    // Billing — paid/unpaid + any recorded payments (client-safe receipt view).
    const isPaid = (userData as any)?.paymentStatus === 'paid';
    const payments = (() => {
        const raw = (userData as any)?.manualPayments;
        return Array.isArray(raw) ? raw : (raw && typeof raw === 'object' ? Object.values(raw) : []);
    })().filter((p: any) => p && (p.amount != null));

    // Financial Health Check — echo the client's saved result on the dashboard.
    const fhc = (userData as any)?.financialHealthCheck;
    const FHC_BAND_COLOR: Record<string, string> = { Excellent: '#15824E', Good: '#1976d2', Stable: '#E6A700', Warning: '#FF7043', Critical: '#D7263D' };

    const activeServices = [
        ...allPkgs.map(pkg => ({
            iconBg: ACCENT_SOFT, iconColor: ACCENT,
            title: pkg.name,
            subtitle: pkg.purchasedAt
                ? `Active since ${new Date(pkg.purchasedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`
                : 'Active',
            badge: 'Active', badgeBg: '#D1FAE5', badgeColor: '#065F46', path: '/my-services',
        })),
        { iconBg: '#F3F4F6', iconColor: '#6B7280', title: 'Add a new service', subtitle: 'Explore payroll, audit, CFO services', badge: null, badgeBg: '', badgeColor: '', path: '/services' },
    ];

    const stripHtml = (html: string) => (html || '').replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();

    const NOTIF_STYLE: Record<string, { iconBg: string; iconColor: string }> = {
        CRITICAL: { iconBg: '#FEE2E2', iconColor: '#DC2626' },
        HIGH:     { iconBg: '#FFF7ED', iconColor: '#EA580C' },
        NORMAL:   { iconBg: ACCENT_SOFT, iconColor: ACCENT },
    };

    const criticalAlerts = (() => {
        const notifs = userData?.notifications;
        if (!notifs || typeof notifs !== 'object') return [];
        return Object.entries(notifs)
            .sort(([, a]: any, [, b]: any) =>
                new Date(b.sentAt || b.timestamp || 0).getTime() - new Date(a.sentAt || a.timestamp || 0).getTime()
            )
            .slice(0, 3)
            .map(([id, n]: [string, any]) => {
                const style = NOTIF_STYLE[n.priority] || NOTIF_STYLE.NORMAL;
                const IconComp = n.type === 'alert' ? AlertCircle : n.type === 'email' ? FileText : MessageSquare;
                return {
                    Icon: IconComp,
                    iconBg: style.iconBg,
                    iconColor: style.iconColor,
                    title: n.title || 'Notification',
                    description: stripHtml(n.preview || n.message || '').slice(0, 120),
                    action: 'View →',
                    actionFn: () => navigate(`/notifications/${id}`),
                };
            });
    })();

    const handleExportSchedule = () => {
        const pad = (n: number) => String(n).padStart(2, '0');
        const toICSDate = (d: Date) => `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}`;
        const exportEvents = usingRealCal
            ? accDeadlines.map(x => ({ d: new Date(x.date), label: x.name }))
            : upcomingEvents.map(e => ({ d: e.dateObj, label: e.label }));
        const icsLines = ['BEGIN:VCALENDAR', 'VERSION:2.0', 'PRODID:-//Conncct//Compliance Calendar//EN'];
        exportEvents.forEach(e => {
            if (isNaN(e.d.getTime())) return;
            icsLines.push('BEGIN:VEVENT', `DTSTART;VALUE=DATE:${toICSDate(e.d)}`, `SUMMARY:${e.label}`, 'END:VEVENT');
        });
        icsLines.push('END:VCALENDAR');
        const blob = new Blob([icsLines.join('\r\n')], { type: 'text/calendar' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'connct-accounting-schedule.ics';
        a.click();
        URL.revokeObjectURL(url);
    };

    return (
        <div className="dash-root flex min-h-screen" style={{ background: activeServiceView === 'consulting' ? '#EFF4FB' : '#FCFAF2' }}>
            <Sidebar />
            <main
                className="flex-1 md:ms-64 overflow-y-auto relative transition-[background] duration-500"
                style={{
                    background: activeServiceView === 'consulting'
                        ? 'linear-gradient(180deg,#E9F1FE 0%,#F1F5FB 30%,#EFF4FB 100%)'
                        : 'linear-gradient(180deg,#FFF8E3 0%,#FCFAF1 30%,#FCFAF2 100%)',
                }}
            >
                <MobileNav onLogout={logout} />

                {(authLoading || loading) ? (
                    <div className="flex items-center justify-center h-screen">
                        <div className="text-center">
                            <div className="animate-spin rounded-full h-12 w-12 border-b-2 mx-auto mb-4" style={{ borderBottomColor: ACCENT }} />
                            <p className="text-slate-600">{t('dashboard.loading')}</p>
                        </div>
                    </div>
                ) : (
                    <div className="mx-auto max-w-[1400px] px-4 md:px-6 lg:px-8 py-4 md:py-6 lg:py-8">

                        {/* Welcome Toast */}
                        {showWelcome && (
                            <div className="fixed top-4 right-4 z-50">
                                <div className="bg-green-500 text-white px-6 py-4 rounded-lg shadow-lg flex items-center gap-3 max-w-md">
                                    <svg className="w-6 h-6 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                    <div className="flex-1">
                                        <p className="font-semibold">{t('dashboard.welcomeBack', { name: userData?.firstName || t('dashboard.userFallback') })}</p>
                                        <p className="text-sm text-green-100">{t('dashboard.signedIn')}</p>
                                    </div>
                                    <button onClick={() => setShowWelcome(false)} className="shrink-0 text-white hover:text-green-100">
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                        </svg>
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Consulting Upsell Modal */}
                        {showConsultingUpsell && (
                            <div
                                className="fixed inset-0 z-50 flex items-center justify-center px-4"
                                style={{ background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(6px)' }}
                                onClick={() => setShowConsultingUpsell(false)}
                            >
                                <div
                                    className="w-full overflow-hidden rounded-2xl shadow-2xl"
                                    style={{ maxWidth: 460 }}
                                    onClick={e => e.stopPropagation()}
                                >
                                    {/* Dark header */}
                                    <div className="relative px-8 pt-8 pb-7" style={{ background: 'linear-gradient(135deg, #0d1f2d 0%, #1a3a4c 100%)' }}>
                                        <button
                                            onClick={() => setShowConsultingUpsell(false)}
                                            className="absolute top-4 right-4 w-8 h-8 rounded-full flex items-center justify-center transition hover:bg-white/10"
                                            style={{ color: 'rgba(255,255,255,0.4)', background: 'rgba(255,255,255,0.06)' }}
                                        >
                                            <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                                                <path d="M1 1l10 10M11 1L1 11" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
                                            </svg>
                                        </button>
                                        <div className="w-11 h-11 rounded-xl flex items-center justify-center mb-5" style={{ background: 'rgba(96,165,250,0.15)' }}>
                                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                                                <path d="M22 12h-4l-3 9L9 3l-3 9H2" stroke="#60A5FA" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                            </svg>
                                        </div>
                                        <h2 className="text-[22px] font-black text-white mb-2 leading-snug">Ready to Raise Capital?</h2>
                                        <p className="text-sm leading-relaxed" style={{ color: 'rgba(255,255,255,0.5)' }}>
                                            Unlock the tools investors expect, and go from compliant to fundable.
                                        </p>
                                    </div>
                                    {/* White body */}
                                    <div className="bg-white px-8 py-7">
                                        <div className="space-y-4 mb-7">
                                            <div className="flex items-center gap-4">
                                                <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: '#F0F7FF' }}><Zap className="w-5 h-5 text-[#1976D2]" /></div>
                                                <div>
                                                    <p className="text-sm font-semibold text-gray-900">Investor Readiness Score</p>
                                                    <p className="text-xs text-gray-400 mt-0.5">See exactly where you stand with investors</p>
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-4">
                                                <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: '#F0F7FF' }}><BarChart3 className="w-5 h-5 text-[#1976D2]" /></div>
                                                <div>
                                                    <p className="text-sm font-semibold text-gray-900">Financial Model</p>
                                                    <p className="text-xs text-gray-400 mt-0.5">Build projections investors trust</p>
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-4">
                                                <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: '#F0F7FF' }}><Target className="w-5 h-5 text-[#1976D2]" /></div>
                                                <div>
                                                    <p className="text-sm font-semibold text-gray-900">Pitch Materials</p>
                                                    <p className="text-xs text-gray-400 mt-0.5">Deck and one-pager ready to send</p>
                                                </div>
                                            </div>
                                        </div>
                                        <button
                                            onClick={() => { markConsultingActivated(); setShowConsultingUpsell(false); navigate(!(userData as any)?.country ? '/Countrysl' : '/readiness-journey'); }}
                                            className="w-full rounded-xl font-bold text-sm text-white transition hover:opacity-90 active:scale-95"
                                            style={{ height: 50, background: 'linear-gradient(135deg, #1565C0 0%, #1976D2 100%)' }}
                                        >
                                            Get started →
                                        </button>
                                        <button
                                            onClick={() => setShowConsultingUpsell(false)}
                                            className="w-full text-sm font-medium transition mt-3 hover:text-gray-600"
                                            style={{ height: 36, color: '#9CA3AF' }}
                                        >
                                            Maybe later
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Accounting Upsell Modal */}
                        {showAccountingUpsell && (
                            <div
                                className="fixed inset-0 z-50 flex items-center justify-center px-4"
                                style={{ background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(6px)' }}
                                onClick={() => setShowAccountingUpsell(false)}
                            >
                                <div className="w-full overflow-hidden rounded-2xl shadow-2xl" style={{ maxWidth: 460 }} onClick={e => e.stopPropagation()}>
                                    {/* Dark header with gold accent */}
                                    <div className="relative px-8 pt-8 pb-7" style={{ background: 'linear-gradient(135deg, #1A1407 0%, #3D2F0E 100%)' }}>
                                        <button
                                            onClick={() => setShowAccountingUpsell(false)}
                                            className="absolute top-4 right-4 w-8 h-8 rounded-full flex items-center justify-center transition hover:bg-white/10"
                                            style={{ color: 'rgba(255,255,255,0.4)', background: 'rgba(255,255,255,0.06)' }}
                                        >
                                            <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M1 1l10 10M11 1L1 11" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/></svg>
                                        </button>
                                        <div className="w-11 h-11 rounded-xl flex items-center justify-center mb-5" style={{ background: 'rgba(230,180,0,0.15)' }}>
                                            <Building2 className="w-5 h-5" style={{ color: '#E6B400' }} />
                                        </div>
                                        <h2 className="text-[22px] font-black text-white mb-2 leading-snug">Activate Accounting</h2>
                                        <p className="text-sm leading-relaxed" style={{ color: 'rgba(255,255,255,0.5)' }}>
                                            Set up your books, tax and compliance, investor-grade from day one.
                                        </p>
                                    </div>
                                    {/* White body */}
                                    <div className="bg-white px-8 py-7">
                                        <div className="space-y-4 mb-7">
                                            <div className="flex items-center gap-4">
                                                <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: ACCENT_SOFT }}><Building2 className="w-5 h-5" style={{ color: ACCENT_ON_SOFT }} /></div>
                                                <div>
                                                    <p className="text-sm font-semibold text-gray-900">Incorporate or Operate</p>
                                                    <p className="text-xs text-gray-400 mt-0.5">Form a new company or run your existing one</p>
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-4">
                                                <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: ACCENT_SOFT }}><ReceiptText className="w-5 h-5" style={{ color: ACCENT_ON_SOFT }} /></div>
                                                <div>
                                                    <p className="text-sm font-semibold text-gray-900">Tax &amp; VAT Compliance</p>
                                                    <p className="text-xs text-gray-400 mt-0.5">Corporate Tax, VAT &amp; regulatory filings handled</p>
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-4">
                                                <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: ACCENT_SOFT }}><FileText className="w-5 h-5" style={{ color: ACCENT_ON_SOFT }} /></div>
                                                <div>
                                                    <p className="text-sm font-semibold text-gray-900">Bookkeeping &amp; Reporting</p>
                                                    <p className="text-xs text-gray-400 mt-0.5">Investor-grade books, always diligence-ready</p>
                                                </div>
                                            </div>
                                        </div>
                                        <button
                                            onClick={markAccountingActivated}
                                            className="w-full rounded-xl font-bold text-sm transition hover:opacity-90 active:scale-95"
                                            style={{ height: 50, background: 'linear-gradient(135deg, #E6B400 0%, #FFD54F 100%)', color: '#17263A' }}
                                        >
                                            Get started →
                                        </button>
                                        <button
                                            onClick={() => setShowAccountingUpsell(false)}
                                            className="w-full text-sm font-medium transition mt-3 hover:text-gray-600"
                                            style={{ height: 36, color: '#9CA3AF' }}
                                        >
                                            Maybe later
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Header */}
                        <header className="mb-6 flex justify-between items-start">
                            <div>
                                <h1 data-tour="dashboard-header" className="text-2xl font-bold text-gray-900">Dashboard</h1>
                                <p className="text-sm text-gray-500 mt-1">Welcome back, here's what's happening today.</p>
                            </div>
                            <div className="flex items-center gap-4">
                                {SHOW_SERVICE_TOGGLE && (
                                <div className="flex flex-col items-end gap-1.5" ref={serviceDropdownRef}>
                                    <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Active Service View</span>
                                    <div className="relative">
                                        <button
                                            onClick={() => setServiceDropdownOpen(o => !o)}
                                            className={`flex items-center gap-2.5 rounded-xl border-2 bg-white pl-4 pr-3 py-2 text-sm font-semibold text-gray-800 cursor-pointer focus:outline-none shadow-sm transition-colors ${
                                                activeServiceView === 'consulting'
                                                    ? 'border-blue-500 focus:ring-2 focus:ring-blue-300 hover:bg-blue-50'
                                                    : 'border-amber-400 focus:ring-2 focus:ring-amber-300 hover:bg-amber-50'
                                            }`}
                                        >
                                            <span>{activeServiceView.charAt(0).toUpperCase() + activeServiceView.slice(1)}</span>
                                            <ChevronRight className={`w-3.5 h-3.5 text-gray-500 transition-transform duration-200 ${serviceDropdownOpen ? '-rotate-90' : 'rotate-90'}`} />
                                        </button>
                                        {serviceDropdownOpen && (
                                            <div className="absolute right-0 mt-2 w-44 bg-white rounded-xl shadow-lg border border-gray-100 py-1.5 z-50">
                                                {(['accounting', 'consulting'] as const).map(service => {
                                                    const isActive = activeServiceView === service;
                                                    const isBlue = service === 'consulting';
                                                    return (
                                                        <button
                                                            key={service}
                                                            onClick={() => {
                                                                setServiceDropdownOpen(false);
                                                                if (service === 'accounting') {
                                                                    // Brand-new accounting users (e.g. coming from consulting) go through the
                                                                    // activation modal → operate/incorporate chooser; activated users just switch view.
                                                                    if (!hasAccountingActivated) { setShowAccountingUpsell(true); }
                                                                    else { setActiveServiceView('accounting'); }
                                                                    return;
                                                                }
                                                                // Consulting: brand-new users go through the onboarding (via the intro modal →
                                                                // /readiness-journey); returning users just switch to the consulting view.
                                                                if (!hasGoalAssessment && !hasIRScore && !hasCrScore && !userData?.consultingActivated) {
                                                                    setShowConsultingUpsell(true);
                                                                } else {
                                                                    markConsultingActivated();
                                                                    setActiveServiceView('consulting');
                                                                }
                                                            }}
                                                            className={`w-full flex items-center justify-between px-4 py-2.5 text-sm font-medium transition-colors ${
                                                                isActive
                                                                    ? isBlue ? 'text-blue-600 bg-blue-50' : 'text-amber-600 bg-amber-50'
                                                                    : 'text-gray-700 hover:bg-gray-50'
                                                            }`}
                                                        >
                                                            <span>{service.charAt(0).toUpperCase() + service.slice(1)}</span>
                                                            {isActive && (
                                                                <svg className={`w-4 h-4 ${isBlue ? 'text-blue-500' : 'text-amber-500'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                                                                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                                                </svg>
                                                            )}
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </div>
                                </div>
                                )}
                                <UserProfileMenu />
                            </div>
                        </header>

                        {/* Getting started — unified onboarding progress (both brands). Hides once complete. */}
                        {(() => {
                            const isCon = activeServiceView === 'consulting';
                            const accent = isCon ? '#1976D2' : '#2159B8';
                            const accentSoft = isCon ? '#EFF6FF' : '#FFF3CE';
                            const pkgCount = (() => { const r: any = (userData as any)?.purchasedPackages; return r ? (Array.isArray(r) ? r : Object.values(r)).filter((p: any) => p?.name).length : 0; })();
                            const setupDone = !!(userData as any)?.businessSetupData?.submittedAt || localStorage.getItem('operateOnboardingComplete') === 'true';
                            const steps: { label: string; done: boolean; cta: string }[] = isCon
                                ? [
                                    { label: 'Set your goal focus', done: hasGoalAssessment, cta: '/readiness-journey' },
                                    { label: 'Get your Investor Readiness score', done: hasIRScore, cta: '/readiness-journey' },
                                    { label: 'Complete your Capital Assessment', done: hasCrScore, cta: '/capital-assessment' },
                                    { label: 'Unlock your Readiness Report', done: reportUnlocked, cta: '/capital-assessment' },
                                ]
                                : [
                                    { label: 'Verify your email', done: !!userData?.emailVerified, cta: '/email-verification' },
                                    { label: 'Complete your company setup', done: setupDone, cta: '/operate-company-info' },
                                    { label: 'Activate an accounting service', done: pkgCount > 0, cta: '/services' },
                                    { label: 'Upload your documents', done: pkgCount > 0 && setupDone, cta: '/data-room' },
                                ];
                            const doneCount = steps.filter(s => s.done).length;
                            if (doneCount >= steps.length) return null;
                            const next = steps.find(s => !s.done);
                            const pct = Math.round((doneCount / steps.length) * 100);
                            return (
                                <div data-tour="get-started" className="mb-6 rounded-2xl border border-gray-100 bg-white p-5 md:p-6 shadow-sm">
                                    <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
                                        <div>
                                            <h2 className="text-base md:text-lg font-extrabold text-gray-900">Get started{isCon ? ' with Conncct' : ''}</h2>
                                            <p className="text-xs text-gray-500 mt-0.5">A few quick steps to unlock everything.</p>
                                        </div>
                                        <span className="text-sm font-black tabular-nums" style={{ color: accent }}>{doneCount}/{steps.length} done</span>
                                    </div>
                                    <div className="w-full h-2 rounded-full bg-gray-100 overflow-hidden mb-4">
                                        <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: accent }} />
                                    </div>
                                    <div className="space-y-2.5 mb-4">
                                        {steps.map((s, i) => (
                                            <div key={i} className="flex items-center gap-3">
                                                <span className="w-6 h-6 rounded-full grid place-items-center flex-none text-[11px] font-bold" style={{ background: s.done ? accent : accentSoft, color: s.done ? '#fff' : accent }}>
                                                    {s.done ? <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12l5 5L19 7" /></svg> : i + 1}
                                                </span>
                                                <span className={`text-sm ${s.done ? 'text-gray-400 line-through' : 'font-semibold text-gray-800'}`}>{s.label}</span>
                                                {!s.done && s === next && (
                                                    <button onClick={() => navigate(s.cta)} className="ml-auto text-xs font-bold hover:underline" style={{ color: accent }}>Start →</button>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                    {next && (
                                        <button onClick={() => navigate(next.cta)} className="w-full sm:w-auto rounded-lg px-6 py-2.5 text-sm font-bold text-white shadow-sm transition hover:opacity-95" style={{ background: accent }}>
                                            {next.label} →
                                        </button>
                                    )}
                                </div>
                            );
                        })()}

                        {/* From your Conncct team — status & documents set by admins in the Command Center */}
                        {(userData as any)?.adminOps && (() => {
                            const ops: any = (userData as any).adminOps;
                            const isAcc = activeServiceView === 'accounting';
                            const acc = ops.acc || {}, con = ops.con || {};
                            const dl = (d: string) => d ? Math.ceil((+new Date(d) - Date.now()) / 86400000) : 99;
                            const fmtd = (d: string) => d ? new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '';
                            let tone = 'green', label = "You're compliant", detail = 'All clear';
                            if (isAcc) {
                                if (!acc.setupComplete) { tone = 'slate'; label = 'Finish setup'; detail = 'Onboarding incomplete'; }
                                else if (acc.overdue) { tone = 'red'; label = 'Action overdue'; detail = `${acc.nextName || 'A filing'} is past due`; }
                                else if (acc.docRequests > 0) { tone = 'amber'; label = 'Documents needed'; detail = `${acc.docRequests} document(s) requested`; }
                                else if (acc.nextDate && dl(acc.nextDate) <= 14) { tone = 'amber'; label = `Due in ${dl(acc.nextDate)} days`; detail = `${acc.nextName} · ${fmtd(acc.nextDate)}`; }
                                else { detail = acc.nextDate ? `Next filing: ${fmtd(acc.nextDate)}` : 'All clear'; }
                            } else {
                                // Single source of truth: prefer the admin's override, else the client's real capital-readiness score.
                                const rd = con.readiness ?? (userData?.capitalReadiness?.score ?? null);
                                // A published, not-yet-downloaded document outranks the score — it's the reason to come back.
                                const pendingDocs = (Array.isArray(con.deliverables) ? con.deliverables : []).filter((d: any) => !d.downloaded);
                                if (pendingDocs.length) { tone = 'green'; label = 'Your report is ready'; detail = pendingDocs[0].name || 'Ready to download'; }
                                else if (rd == null) { tone = 'slate'; label = "Let's begin"; detail = 'Start your assessment'; }
                                else if (rd >= 75) { label = 'Investor-ready'; detail = `Capital readiness ${rd}/100`; }
                                else if (rd >= 50) { tone = 'blue'; label = 'Nearly investor-ready'; detail = `${rd}/100 · close the last gaps`; }
                                else { tone = 'amber'; label = 'Building readiness'; detail = `${rd}/100 · key gaps remain`; }
                            }
                            const deliverables = (isAcc ? (Array.isArray(acc.deliverables) ? acc.deliverables : []) : (Array.isArray(con.deliverables) ? con.deliverables : []));
                            const TONE: any = { green: { bg: '#E6F2EC', ink: '#0F6E42', dot: '#15824E' }, blue: { bg: '#E8F3FD', ink: '#0C447C', dot: '#1976D2' }, amber: { bg: '#FBF1D6', ink: '#8A6400', dot: '#E69500' }, red: { bg: '#FBE9E7', ink: '#9B2D20', dot: '#C0392B' }, slate: { bg: '#EEF1F4', ink: '#42505F', dot: '#5B6B7F' } }[tone];
                            return (
                                <div className="mb-6 rounded-2xl border border-gray-100 bg-white p-4 md:p-5 shadow-sm">
                                    <div className="text-[11px] font-bold uppercase tracking-wide text-gray-400 mb-3">From your Conncct team</div>
                                    <div className="flex items-start gap-3 rounded-xl p-3.5" style={{ background: TONE.bg }}>
                                        <span className="w-2.5 h-2.5 rounded-full mt-1.5 flex-none" style={{ background: TONE.dot }} />
                                        <div><b className="text-sm font-extrabold" style={{ color: TONE.ink }}>{label}</b><p className="text-xs mt-0.5" style={{ color: TONE.ink }}>{detail}</p></div>
                                    </div>
                                    {/* Itemized requests the client must action (onboarding docs + active-client ID renewals / updated docs / info) */}
                                    {(() => {
                                        const src = isAcc ? acc.requests : con.requests;
                                        const reqs = (Array.isArray(src) ? src : []).filter((r: any) => r && r.status !== 'received');
                                        if (!reqs.length) return null;
                                        const KL: any = { document: 'Document', 'id-renewal': 'ID renewal', update: 'Updated doc', info: 'Info' };
                                        return (
                                            <div className="mt-3">
                                                <div className="text-[11px] font-bold uppercase tracking-wide text-gray-400 mb-2">Action needed: please provide</div>
                                                <div className="space-y-2">
                                                    {reqs.map((r: any, i: number) => (
                                                        <div key={r.id || i} className="flex items-center gap-3 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
                                                            <span className="text-[10px] font-extrabold uppercase tracking-wide text-amber-700 bg-amber-100 rounded px-2 py-1 flex-none">{KL[r.kind] || 'Request'}</span>
                                                            <div className="flex-1 min-w-0"><b className="text-sm block truncate text-gray-900">{r.label || 'Requested item'}</b>{r.note && <span className="text-[11px] text-gray-500 block truncate">{r.note}</span>}</div>
                                                            <a href={isAcc ? '/accounting/data-room' : '/data-room'} className="text-xs font-bold text-amber-700 hover:text-amber-800 flex-none">Upload →</a>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        );
                                    })()}
                                    {deliverables.length > 0 && (
                                        <div className="mt-3 space-y-2">
                                            {deliverables.map((d: any, i: number) => (
                                                <div key={i} className="flex items-center gap-3 bg-gray-50 rounded-lg px-3 py-2">
                                                    <span className="acc-soft-bg acc-ink w-8 h-8 rounded-lg grid place-items-center text-[10px] font-extrabold flex-none">PDF</span>
                                                    <div className="flex-1 min-w-0"><b className="text-sm block truncate text-gray-900">{d.name}</b><span className="text-[11px] text-gray-400">{fmtd(d.date)}</span></div>
                                                    {d.url && d.url !== '#' && <a href={d.url} target="_blank" rel="noreferrer" className="acc-text text-xs font-bold">Download →</a>}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            );
                        })()}

                        {/* ═══════════════════ ACCOUNTING VIEW ═══════════════════ */}
                        {activeServiceView === 'accounting' ? (
                            <div className="grid gap-5 lg:grid-cols-3">

                                {/* ── Left Column ── */}
                                <div className="lg:col-span-2 space-y-5">

                                    {/* Your order — business-setup order + live status tracker */}
                                    {hasBusinessSetup && bsd && (
                                        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5">
                                            <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
                                                <div className="min-w-0">
                                                    <p className="text-[11px] font-bold uppercase tracking-wide text-gray-400">Your order</p>
                                                    <h3 className="text-base font-extrabold text-gray-900 mt-0.5">{bsd.type === 'incorporate' ? 'Company Incorporation' : 'Operate: Existing Company'}</h3>
                                                    <p className="text-xs text-gray-500 mt-0.5">
                                                        {[bsd.planName, bsd.emirate].filter(Boolean).join(' · ')}
                                                        {bsd.total != null && <> · <b className="text-gray-700">AED {Number(bsd.total).toLocaleString()}</b></>}
                                                    </p>
                                                </div>
                                                {orderCancelled
                                                    ? <span className="text-[11px] font-bold px-2.5 py-1 rounded-full bg-red-100 text-red-700 shrink-0">Cancelled</span>
                                                    : <span className="text-[11px] font-bold px-2.5 py-1 rounded-full shrink-0" style={{ background: orderStepIdx >= 3 ? '#D1FAE5' : '#FEF3C7', color: orderStepIdx >= 3 ? '#065F46' : '#8A6008' }}>{ORDER_STEPS[orderStepIdx]}</span>}
                                            </div>
                                            {orderCancelled ? (
                                                <p className="text-sm text-gray-500">This order was cancelled. Please contact your advisor if that's unexpected.</p>
                                            ) : (
                                                <div className="flex items-start">
                                                    {ORDER_STEPS.map((label, i) => {
                                                        const done = i < orderStepIdx, current = i === orderStepIdx;
                                                        return (
                                                            <div key={i} className="flex-1 flex flex-col items-center relative">
                                                                {i > 0 && <div className="absolute top-3.5 left-[-50%] right-1/2 h-0.5" style={{ background: i <= orderStepIdx ? ACCENT : '#E5E7EB' }} />}
                                                                <span className="relative z-10 w-7 h-7 rounded-full grid place-items-center text-[11px] font-bold" style={{ background: done || current ? ACCENT : '#F3F4F6', color: done || current ? '#fff' : '#9CA3AF' }}>
                                                                    {done ? <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12l5 5L19 7" /></svg> : i + 1}
                                                                </span>
                                                                <span className={`text-[10px] mt-1.5 font-semibold text-center leading-tight ${current ? 'text-amber-700' : done ? 'text-gray-600' : 'text-gray-400'}`}>{label}</span>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            )}
                                            {orderStatusRaw === 'awaiting_docs' && (
                                                <button onClick={() => navigate('/accounting/data-room')} className="mt-4 text-xs font-bold text-amber-700 hover:underline">Your team needs documents from you → Upload</button>
                                            )}
                                        </div>
                                    )}

                                    {/* Billing — payment status + receipts */}
                                    {(isPaid || payments.length > 0) && (
                                        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5">
                                            <div className="flex items-center justify-between gap-3 mb-3">
                                                <div>
                                                    <p className="text-[11px] font-bold uppercase tracking-wide text-gray-400">Billing</p>
                                                    <h3 className="text-base font-extrabold text-gray-900 mt-0.5">Payments &amp; receipts</h3>
                                                </div>
                                                <span className="text-[11px] font-bold px-2.5 py-1 rounded-full" style={{ background: isPaid ? '#D1FAE5' : '#F3F4F6', color: isPaid ? '#065F46' : '#6B7280' }}>{isPaid ? 'Paid' : 'Not paid'}</span>
                                            </div>
                                            {payments.length > 0 ? (
                                                <div className="space-y-2">
                                                    {payments.slice().reverse().map((p: any, i: number) => (
                                                        <div key={i} className="flex items-center justify-between gap-3 bg-gray-50 rounded-lg px-3 py-2">
                                                            <div className="flex items-center gap-2.5 min-w-0">
                                                                <span className="w-8 h-8 rounded-lg bg-green-100 grid place-items-center flex-none">
                                                                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#15824E" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12l5 5L19 7" /></svg>
                                                                </span>
                                                                <div className="min-w-0">
                                                                    <b className="text-sm text-gray-900">{(p.currency || 'AED')} {Number(p.amount).toLocaleString()}</b>
                                                                    <p className="text-[11px] text-gray-400">{String(p.method || '').replace(/_/g, ' ').replace(/^./, (c: string) => c.toUpperCase())}{p.at ? ` · ${new Date(p.at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}` : ''}</p>
                                                                </div>
                                                            </div>
                                                            <span className="text-[10px] font-bold text-green-700 bg-green-50 px-2 py-0.5 rounded-full shrink-0">Received</span>
                                                        </div>
                                                    ))}
                                                </div>
                                            ) : (
                                                <p className="text-sm text-gray-500">Your account is marked as paid. Receipts will appear here.</p>
                                            )}
                                        </div>
                                    )}

                                    {/* Financial Health Check — echoed from the client's own check */}
                                    {fhc && typeof fhc.score === 'number' && (
                                        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5">
                                            <div className="flex items-center justify-between gap-3">
                                                <div className="flex items-center gap-4 min-w-0">
                                                    <div className="w-14 h-14 rounded-2xl grid place-items-center shrink-0" style={{ background: (FHC_BAND_COLOR[fhc.band] || '#1976d2') + '1A' }}>
                                                        <span className="text-xl font-black" style={{ color: FHC_BAND_COLOR[fhc.band] || '#1976d2' }}>{fhc.score}</span>
                                                    </div>
                                                    <div className="min-w-0">
                                                        <p className="text-[11px] font-bold uppercase tracking-wide text-gray-400">Financial Health</p>
                                                        <h3 className="text-base font-extrabold text-gray-900 leading-tight">{fhc.band || '-'}{fhc.profitable ? ' · Profitable' : fhc.preRevenue ? ' · Pre-revenue' : ''}</h3>
                                                        <p className="text-xs text-gray-500 mt-0.5">
                                                            {fhc.runway != null ? `${fhc.runway} mo runway` : (fhc.profitable ? 'Profitable' : '')}
                                                            {fhc.computedAt ? ` · checked ${new Date(fhc.computedAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}` : ''}
                                                        </p>
                                                    </div>
                                                </div>
                                                <button onClick={() => navigate('/financial-health-check')} className="text-xs font-bold text-amber-700 hover:underline shrink-0">Re-check →</button>
                                            </div>
                                        </div>
                                    )}

                                    {/* UAE Compliance Hub — dark hero card (Accounting) */}
                                    <div
                                        className="relative rounded-2xl overflow-hidden flex flex-col md:flex-row items-stretch"
                                        style={{ background: 'linear-gradient(135deg, #0b1a0f 0%, #122018 60%, #0a1c12 100%)' }}
                                    >
                                        {/* Decorative glow */}
                                        <div className="absolute top-0 left-1/3 w-64 h-64 rounded-full opacity-10 blur-3xl pointer-events-none" style={{ backgroundColor: '#22C55E' }} />

                                        {/* Left: Content */}
                                        <div className="relative z-10 flex-1 p-8 md:p-10 flex flex-col justify-between">
                                            <div>
                                                <div className="inline-flex items-center gap-2 rounded-full px-3.5 py-1.5 mb-6 w-fit" style={{ background: 'rgba(34,197,94,0.15)', border: '1px solid rgba(34,197,94,0.35)' }}>
                                                    <Sparkles className="w-3 h-3 text-green-400" />
                                                    <span className="text-xs font-bold text-green-400 tracking-wide">ACCOUNTING & TAX</span>
                                                </div>
                                                <h2 className="text-4xl md:text-5xl font-black text-white mb-4 leading-[1.1] tracking-tight">
                                                    UAE Compliance<br />Made Simple
                                                </h2>
                                                <p className="text-sm text-slate-400 leading-relaxed max-w-sm">
                                                    Bookkeeping, VAT filing, and Corporate Tax, handled end-to-end by your dedicated Conncct team.
                                                </p>
                                            </div>
                                            <div>
                                                <div className="flex items-center gap-8 mb-7">
                                                    {[{ value: '200+', label: 'UAE Clients' }, { value: '98%', label: 'On-time Filing' }].map((s, i) => (
                                                        <div key={i} className="flex items-center gap-8">
                                                            {i > 0 && <div className="w-px h-7 bg-white/10" />}
                                                            <div>
                                                                <p className="text-xl font-black text-white leading-none">{s.value}</p>
                                                                <p className="text-[9px] text-slate-500 uppercase tracking-wider mt-1">{s.label}</p>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                                <button
                                                    onClick={() => navigate('/my-services')}
                                                    className="inline-flex items-center gap-2 rounded-xl px-7 py-3.5 text-sm font-bold text-gray-900 w-fit hover:brightness-110 active:scale-95 transition-all shadow-lg"
                                                    style={{ backgroundColor: '#FACC15' }}
                                                >
                                                    View My Services →
                                                </button>
                                            </div>
                                        </div>

                                        {/* Right: Glass panel */}
                                        <div className="relative z-10 p-5 flex items-stretch md:w-80 shrink-0">
                                            <div className="w-full rounded-2xl p-7 flex flex-col" style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)', backdropFilter: 'blur(16px)' }}>
                                                {/* Header */}
                                                <div className="flex items-center justify-between mb-6">
                                                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Compliance Status</p>
                                                    <span className="text-[10px] font-semibold px-2.5 py-1 rounded-full" style={{ backgroundColor: setupAllDone ? 'rgba(21,130,78,0.2)' : 'rgba(230,180,0,0.2)', color: setupAllDone ? '#15824E' : '#E6B400' }}>
                                                        {setupAllDone ? 'Active' : setupDoneCount > 0 ? 'In Progress' : 'Setup Needed'}
                                                    </span>
                                                </div>

                                                {/* Ring */}
                                                <div className="flex flex-col items-center mx-auto mb-2">
                                                    <div className="relative w-36 h-36">
                                                        <svg className="w-36 h-36 -rotate-90" viewBox="0 0 144 144">
                                                            <circle cx="72" cy="72" r="64" fill="none" stroke="rgba(255,255,255,0.04)" strokeWidth="4" />
                                                            <circle cx="72" cy="72" r="54" fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="11" />
                                                            <circle cx="72" cy="72" r="54" fill="none"
                                                                stroke={setupAllDone ? '#22C55E' : '#E6B400'} strokeWidth="11"
                                                                strokeDasharray={2 * Math.PI * 54}
                                                                strokeDashoffset={2 * Math.PI * 54 * (1 - setupFrac)}
                                                                strokeLinecap="round"
                                                                style={{ transition: 'stroke-dashoffset 1.4s cubic-bezier(0.4,0,0.2,1)' }}
                                                            />
                                                        </svg>
                                                        <div className="absolute inset-0 flex items-center justify-center">
                                                            <span className="text-5xl font-black text-white leading-none">{setupPct}</span>
                                                        </div>
                                                    </div>
                                                    <span className="text-[11px] font-bold tracking-widest mt-2" style={{ color: setupAllDone ? '#22C55E' : '#E6B400' }}>SETUP PROGRESS</span>
                                                </div>

                                                {/* Thin progress bar */}
                                                <div className="w-full rounded-full h-1 mb-6 mx-auto max-w-[140px]" style={{ backgroundColor: 'rgba(255,255,255,0.08)' }}>
                                                    <div className="h-1 rounded-full transition-all duration-1000" style={{ width: `${setupPct}%`, backgroundColor: setupAllDone ? '#22C55E' : '#E6B400' }} />
                                                </div>

                                                {/* Steps */}
                                                <div className="space-y-3 mt-auto">
                                                    {[
                                                        { label: 'Company Setup', done: hasBusinessSetup, path: localStorage.getItem('setupService') === 'operate' ? '/operate-company-info' : '/accounting/select' },
                                                        { label: 'Documents Uploaded', done: hasUploadedDocs, path: '/accounting/data-room' },
                                                        { label: 'Compliance Active', done: hasActiveService, path: '/services' },
                                                    ].map(step => (
                                                        <button key={step.label}
                                                            onClick={() => !step.done && navigate(step.path)}
                                                            className="w-full flex items-center gap-3 text-left hover:opacity-80 transition-opacity"
                                                            style={{ cursor: step.done ? 'default' : 'pointer' }}
                                                        >
                                                            <div className="w-5 h-5 rounded-full flex items-center justify-center shrink-0"
                                                                style={{ backgroundColor: step.done ? '#22C55E' : 'rgba(255,255,255,0.06)', border: step.done ? 'none' : '1.5px solid rgba(255,255,255,0.15)' }}>
                                                                {step.done
                                                                    ? <svg className="w-3 h-3 text-white" viewBox="0 0 12 12" fill="none"><path d="M2 6.5l2.8 2.8L10 3.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>
                                                                    : <div className="w-1.5 h-1.5 rounded-full bg-slate-600" />
                                                                }
                                                            </div>
                                                            <span className={`text-sm font-semibold flex-1 ${step.done ? 'text-white' : 'text-slate-500'}`}>{step.label}</span>
                                                            {!step.done && <span className="text-[11px] acc-text font-bold">Start →</span>}
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Upload Documents — a prompt, so it retires once there is
                                        anything in the data room. `=== false` on purpose: while the
                                        Storage check is still out this is null, and rendering then
                                        would show the prompt to someone who has already uploaded. */}
                                    {hasUploadedDocs === false && <div
                                        onClick={() => navigate('/accounting/data-room')}
                                        className="cursor-pointer bg-white rounded-2xl border border-slate-100 shadow-sm p-5 flex items-center justify-between hover:shadow-md transition-shadow"
                                    >
                                        <div className="flex items-center gap-4">
                                            <div className="acc-soft-bg h-11 w-11 rounded-xl flex items-center justify-center">
                                                <img src={cloud} alt="Upload" className="w-5 h-5 opacity-70" loading="lazy" />
                                            </div>
                                            <div>
                                                <h3 className="text-sm font-semibold text-gray-900">{t('dashboard.uploadDocuments')}</h3>
                                                <p className="text-xs text-gray-400 mt-0.5">{t('dashboard.uploadDocumentsDesc')}</p>
                                            </div>
                                        </div>
                                        <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gray-50">
                                            <ChevronRight className="w-4 h-4 text-gray-400" />
                                        </div>
                                    </div>}

                                    {/* Compliance Calendar */}
                                    <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">

                                        {/* ── Header ── */}
                                        <div className="px-4 pt-2 pb-1.5 flex items-center justify-between border-b border-gray-50">
                                            <div className="flex items-center gap-2.5 text-left">
                                                <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ background: 'linear-gradient(135deg,#17263A 0%,#2c4a6e 100%)' }}>
                                                    <Calendar className="w-4 h-4 text-white" />
                                                </div>
                                                <div>
                                                    <p className="text-[13px] font-bold text-gray-900 leading-tight">Compliance Calendar</p>
                                                    <p className="text-[10px] text-gray-400 font-medium mt-0.5">{usingRealCal ? 'Your filing deadlines · set by your team' : 'Filings, payroll & close dates'}</p>
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-0.5">
                                                {calendarOffset !== 0 && (
                                                    <button onClick={() => setCalendarOffset(0)} className="text-[10px] font-bold text-amber-500 hover:text-amber-600 px-2 py-1 rounded-lg hover:bg-amber-50 transition mr-1">Today</button>
                                                )}
                                                <button onClick={() => setCalendarOffset(o => o - 1)} className="w-6 h-6 rounded-lg flex items-center justify-center hover:bg-gray-100 transition text-gray-400">
                                                    <ChevronLeft className="w-3.5 h-3.5" />
                                                </button>
                                                <span className="text-[11px] font-bold text-gray-700 px-1 min-w-[72px] text-center">{calLabel}</span>
                                                <button onClick={() => setCalendarOffset(o => o + 1)} className="w-6 h-6 rounded-lg flex items-center justify-center hover:bg-gray-100 transition text-gray-400">
                                                    <ChevronRight className="w-3.5 h-3.5" />
                                                </button>
                                            </div>
                                        </div>

                                        {/* Documents requested by the team */}
                                        {docRequests > 0 && (
                                            <button onClick={() => navigate('/accounting/data-room')} className="w-full flex items-center gap-2.5 px-4 py-2.5 border-b border-amber-100 bg-amber-50/70 hover:bg-amber-50 transition text-left">
                                                <span className="w-6 h-6 rounded-lg grid place-items-center shrink-0" style={{ background: ACCENT_SOFT }}><FontAwesomeIcon icon={faInfo} className="h-3 w-3" style={{ color: ACCENT_ON_SOFT }} /></span>
                                                <span className="flex-1 min-w-0 text-[12px] font-bold text-amber-900">{docRequests} document{docRequests !== 1 ? 's' : ''} requested by your team</span>
                                                <span className="text-[11px] font-bold text-amber-700 whitespace-nowrap">Upload →</span>
                                            </button>
                                        )}

                                        {/* ── Body: compact month grid + upcoming, side by side ── */}
                                        <div className="flex flex-col md:flex-row">
                                            {/* Month grid */}
                                            <div className="px-3.5 py-1.5 md:w-[320px] md:shrink-0 md:border-r border-gray-50">
                                                <div className="grid grid-cols-7 mb-[1px]">
                                                    {['Mo','Tu','We','Th','Fr','Sa','Su'].map((d, i) => (
                                                        <div key={i} className={`text-center text-[9px] font-bold uppercase tracking-wide ${i >= 5 ? 'text-gray-300' : 'text-gray-400'}`}>{d}</div>
                                                    ))}
                                                </div>
                                                <div className="grid grid-cols-7">
                                                    {calendarGrid.map((cell, i) => {
                                                        const dotColor = cell.events[0]?.dot;
                                                        const isWeekend = i % 7 >= 5;
                                                        return (
                                                            <div key={i} className="relative flex items-center justify-center h-[24px]">
                                                                <div
                                                                    className="w-[21px] h-[21px] rounded-full flex items-center justify-center text-[10.5px] font-bold transition-all"
                                                                    style={
                                                                        cell.isToday
                                                                            ? { background: 'linear-gradient(135deg,#17263A,#2c4a6e)', color: '#FFD54F', boxShadow: '0 2px 7px rgba(23,38,58,.28)' }
                                                                            : cell.isCurrentMonth
                                                                                ? { color: isWeekend ? '#9CA3AF' : '#374151' }
                                                                                : { color: '#E5E7EB' }
                                                                    }
                                                                >
                                                                    {cell.date}
                                                                </div>
                                                                {dotColor && cell.isCurrentMonth && (
                                                                    <div className="absolute bottom-[-1px] left-1/2 -translate-x-1/2 flex gap-[3px]">
                                                                        {cell.events.slice(0, 3).map((ev, j) => (
                                                                            <div key={j} className="w-[5px] h-[5px] rounded-full ring-1 ring-white" style={{ backgroundColor: ev.dot }} />
                                                                        ))}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>

                                            {/* Upcoming */}
                                            <div className="flex-1 px-3.5 py-1.5 md:pl-4 border-t md:border-t-0 border-gray-50 min-w-0">
                                                <p className="text-[9px] font-bold text-gray-400 uppercase tracking-wider mb-1.5">Upcoming</p>
                                                {upcomingEvents.length > 0 ? (
                                                    <div className="space-y-0.5">
                                                        {upcomingEvents.map((ev, i) => (
                                                            <div key={i} className="flex items-center gap-2.5 rounded-lg px-2 py-1 hover:bg-gray-50 transition-colors">
                                                                <div className="w-6 h-6 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: ev.dot + '1A' }}>
                                                                    <div className="w-[6px] h-[6px] rounded-full" style={{ backgroundColor: ev.dot }} />
                                                                </div>
                                                                <div className="flex-1 min-w-0">
                                                                    <p className="text-[12px] font-bold text-gray-800 leading-tight truncate">{ev.label}</p>
                                                                    <p className="text-[10px] text-gray-400 font-medium">{ev.dateLabel}</p>
                                                                </div>
                                                                {ev.isThisWeek && (
                                                                    <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-600 border border-amber-200 shrink-0 whitespace-nowrap">This week</span>
                                                                )}
                                                            </div>
                                                        ))}
                                                    </div>
                                                ) : (
                                                    <p className="text-[11px] text-gray-400 font-medium">{usingRealCal ? 'No filings scheduled yet. Your advisor will add your deadlines.' : 'Nothing scheduled this month.'}</p>
                                                )}
                                            </div>
                                        </div>

                                        {/* ── Footer legend ── */}
                                        <div className="flex items-center justify-between px-4 py-1 border-t border-gray-50">
                                            {usingRealCal ? (
                                                <span className="text-[10px] text-gray-400 font-semibold">Set by your Conncct team</span>
                                            ) : (
                                                <div className="flex items-center gap-3">
                                                    {[{ c:'#3B82F6', l:'VAT' },{ c:'#EF4444', l:'Payroll' },{ c:'#10B981', l:'Recon' },{ c:'#F59E0B', l:'Close' }].map(item => (
                                                        <span key={item.l} className="flex items-center gap-1 text-[10px] text-gray-400 font-semibold">
                                                            <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: item.c }} />{item.l}
                                                        </span>
                                                    ))}
                                                </div>
                                            )}
                                            <button onClick={handleExportSchedule} className="inline-flex items-center gap-1 text-[10px] font-semibold text-gray-400 hover:text-gray-600 transition"><Download className="w-3 h-3" /> Export</button>
                                        </div>
                                    </div>

                                    {/* Active Services & Activity.
                                        The tour anchor is the whole card: pinned to the status dot it was,
                                        the spotlight framed an 8px dot and the tour card then covered the
                                        very heading it was describing. */}
                                    <div data-tour="services" className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
                                        {/* Section header */}
                                        <div className="flex items-center justify-between px-5 pt-4 pb-3 border-b border-gray-50">
                                            <div className="flex items-center gap-2">
                                                <div className="w-2 h-2 rounded-full bg-emerald-400 shadow-sm" style={{ boxShadow: '0 0 0 3px rgba(52,211,153,.2)' }} />
                                                <h3 className="text-sm font-bold text-gray-900">Active Services</h3>
                                                {allPkgs.length > 0 && (
                                                    <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-100">{allPkgs.length}</span>
                                                )}
                                            </div>
                                            <button onClick={() => navigate('/my-services')} className="text-xs font-semibold text-amber-500 hover:text-amber-600 transition">View All</button>
                                        </div>

                                        <div className="divide-y divide-gray-50">
                                            {activeServices.map((svc, i) => {
                                                const isAdd = !svc.badge;
                                                return (
                                                    <div
                                                        key={i}
                                                        onClick={() => navigate(svc.path)}
                                                        className={`flex items-center justify-between px-5 py-3.5 cursor-pointer transition-colors ${isAdd ? 'hover:bg-amber-50/50' : 'hover:bg-gray-50'}`}
                                                    >
                                                        <div className="flex items-center gap-3">
                                                            {isAdd ? (
                                                                <div className="w-9 h-9 rounded-xl flex items-center justify-center text-sm font-bold shrink-0 border-2 border-dashed border-gray-200 text-gray-400">
                                                                    +
                                                                </div>
                                                            ) : (
                                                                <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0" style={{ background: 'linear-gradient(135deg,#FFD54F,#E6B400)' }}>
                                                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#17263A" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                                                                        <rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/>
                                                                    </svg>
                                                                </div>
                                                            )}
                                                            <div>
                                                                <p className={`text-sm font-semibold leading-tight ${isAdd ? 'text-gray-500' : 'text-gray-900'}`}>{svc.title}</p>
                                                                <p className="text-[11px] text-gray-400 mt-0.5 font-medium">{svc.subtitle}</p>
                                                            </div>
                                                        </div>
                                                        {svc.badge ? (
                                                            <div className="flex items-center gap-1.5 shrink-0">
                                                                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                                                                <span className="text-[11px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-100 px-2.5 py-1 rounded-full">Active</span>
                                                            </div>
                                                        ) : (
                                                            <ChevronRight className="w-4 h-4 text-gray-300 shrink-0" />
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>

                                    {/* CPA Advisor */}
                                    <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5">
                                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                                            <div className="flex items-center gap-4">
                                                <div className="w-11 h-11 rounded-xl bg-amber-50 flex items-center justify-center shrink-0">
                                                    <img src={speak} alt="" className="w-6 h-6 object-contain" loading="lazy" />
                                                </div>
                                                <div>
                                                    <h3 className="text-sm font-bold text-gray-900">Talk to a CPA advisor</h3>
                                                    <p className="text-xs text-gray-500 mt-0.5">Everything in 'Raise & Close' + strategic execution support</p>
                                                </div>
                                            </div>
                                            <button
                                                onClick={() => navigate('/book-consultation')}
                                                className="shrink-0 rounded-xl border border-gray-200 bg-gray-50 px-4 py-2.5 text-xs font-semibold text-gray-700 hover:bg-gray-100 transition whitespace-nowrap"
                                            >
                                                Get a Consultancy: Only 99$/hour
                                            </button>
                                        </div>
                                    </div>

                                    {/* News & Advisory — our own articles first (read in-app), then curated/industry news */}
                                    <NewsSpotlight />
                                </div>

                                {/* ── Right Column ── */}
                                <div className="space-y-4">

                                    {/* Notification Center — anchor the tour to the card, not its icon tile. */}
                                    <div data-tour="notifications" className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5">
                                        <div className="flex items-center gap-2.5 mb-5">
                                            <div className="acc-soft-bg w-7 h-7 rounded-lg flex items-center justify-center">
                                                <MessageSquare className="w-4 h-4" style={{ color: ACCENT }} />
                                            </div>
                                            <h3 className="text-sm font-bold text-gray-900">Notification Center </h3>
                                        </div>
                                        <div className="space-y-4">
                                            {criticalAlerts.length === 0 ? (
                                                <div className="flex flex-col items-center justify-center py-4 gap-1.5">
                                                    <MessageSquare className="w-7 h-7 text-gray-200" />
                                                    <p className="text-xs text-gray-400 text-center">No notifications yet</p>
                                                </div>
                                            ) : (
                                                criticalAlerts.map(({ Icon, iconBg, iconColor, title, description, action, actionFn }, i) => (
                                                    <div key={i} className="flex items-start gap-3">
                                                        <div className="w-8 h-8 rounded-lg shrink-0 flex items-center justify-center mt-0.5" style={{ backgroundColor: iconBg }}>
                                                            <Icon className="w-4 h-4" style={{ color: iconColor }} />
                                                        </div>
                                                        <div className="flex-1 min-w-0">
                                                            <p className="text-xs font-semibold text-gray-900 leading-snug">{title}</p>
                                                            {description && <p className="text-[11px] text-gray-500 mt-0.5 leading-snug">{description}</p>}
                                                            <button onClick={actionFn} className="acc-text text-[11px] font-bold mt-1.5 transition">{action}</button>
                                                        </div>
                                                    </div>
                                                ))
                                            )}
                                        </div>
                                        <button onClick={() => navigate('/notifications')} className="mt-5 w-full text-center text-[11px] font-medium text-gray-400 hover:text-gray-600 transition">
                                            View all notifications
                                        </button>
                                    </div>

                                    {/* Free Consultation */}
                                    <div className="rounded-2xl p-5" style={{ background: 'linear-gradient(135deg, #FFF2D6 0%, #FCE3B0 100%)' }}>
                                        <div className="w-9 h-9 rounded-xl bg-white/60 flex items-center justify-center mb-3">
                                            <img src={cam} alt="" className="w-5 h-5 object-contain" loading="lazy" />
                                        </div>
                                        <h3 className="text-base font-bold text-gray-900 mb-1.5">{t('dashboard.freeConsultation')}</h3>
                                        <p className="text-xs text-gray-700 mb-5 leading-relaxed">{t('dashboard.freeConsultationDesc')}</p>
                                        <button onClick={() => navigate('/book-consultation')} className="btn-acc w-full rounded-xl py-2.5 text-sm font-bold transition shadow-sm">
                                            {t('dashboard.bookNow')}
                                        </button>
                                    </div>

                                    {/* Tax & Corporate Filings */}
                                    <div className="rounded-2xl p-5" style={{ background: 'linear-gradient(135deg, #FFF7E0 0%, #fef3c7 100%)' }}>
                                        <div className="w-9 h-9 rounded-xl bg-white/60 flex items-center justify-center mb-3">
                                            <img src={doc} alt="" className="w-5 h-5 object-contain" loading="lazy" />
                                        </div>
                                        <h3 className="text-base font-bold text-gray-900 mb-1.5">{t('dashboard.taxFilings')}</h3>
                                        <p className="text-xs text-gray-700 mb-5 leading-relaxed">{t('dashboard.taxFilingsDesc')}</p>
                                        <button onClick={() => navigate(requestPath('tax-filings'))} className="w-full rounded-xl bg-[#FACC15] py-2.5 text-sm font-bold text-gray-900 hover:bg-yellow-400 transition shadow-sm">
                                            {t('dashboard.bookNow')}
                                        </button>
                                    </div>

                                    {/* Corporate Services Support */}
                                    <div className="rounded-2xl p-5 bg-white border border-slate-100 shadow-sm">
                                        <div className="w-9 h-9 rounded-xl bg-slate-100 flex items-center justify-center mb-3">
                                            <Shield className="w-5 h-5 text-slate-600" />
                                        </div>
                                        <h3 className="text-base font-bold text-gray-900 mb-1.5">Corporate Services Support</h3>
                                        <p className="text-xs text-gray-600 mb-5 leading-relaxed">
                                            Whether you're just starting or scaling your business, our legal team helps you stay protected and investor-ready.
                                        </p>
                                        <button onClick={() => navigate(requestPath('corporate-services'))} className="w-full rounded-xl border border-gray-200 bg-gray-50 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-100 transition">
                                            Start Now
                                        </button>
                                    </div>

                                    {/* Documents & Consultations */}
                                    <div className="grid grid-cols-2 gap-3">
                                        <div onClick={() => navigate('/accounting/data-room')} className="cursor-pointer bg-white rounded-2xl border border-slate-100 shadow-sm p-4 flex flex-col items-center justify-center text-center hover:shadow-md transition-shadow h-28">
                                            <div className="w-7 h-7 mb-2.5">
                                                <img src={doc} alt="" className="w-full h-full object-contain" loading="lazy" />
                                            </div>
                                            <p className="text-xs font-semibold text-gray-800">{t('dashboard.documents')}</p>
                                        </div>
                                        <div onClick={() => navigate('/book-consultation')} className="cursor-pointer bg-white rounded-2xl border border-slate-100 shadow-sm p-4 flex flex-col items-center justify-center text-center hover:shadow-md transition-shadow h-28">
                                            <div className="w-7 h-7 mb-2.5">
                                                <img src={cam} alt="" className="w-full h-full object-contain" loading="lazy" />
                                            </div>
                                            <p className="text-xs font-semibold text-gray-800">{t('dashboard.consultations')}</p>
                                        </div>
                                    </div>

                                    {/* Refer & Earn */}
                                    <div className="flex items-center justify-center py-1">
                                        <button onClick={() => navigate('/refer-and-earn')} className="flex items-center gap-1.5 text-xs font-semibold text-gray-400 hover:text-gray-700 transition">
                                            <Heart className="w-3.5 h-3.5 text-red-400" /> Refer &amp; Earn Rewards
                                        </button>
                                    </div>

                                    {/* External Auditing */}
                                    <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5">
                                        <div className="acc-soft-bg w-9 h-9 rounded-xl flex items-center justify-center mb-3">
                                            <svg className="w-5 h-5" style={{ color: ACCENT }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                                            </svg>
                                        </div>
                                        <h3 className="text-base font-bold text-gray-900 mb-1.5">External Auditing</h3>
                                        <p className="text-xs text-gray-600 mb-5 leading-relaxed">
                                            Pitch deck, financial model, valuation, and a clean data room, so you can raise with confidence.
                                        </p>
                                        <button onClick={() => navigate(requestPath('external-audit'))} className="btn-acc w-full rounded-xl py-2.5 text-sm font-bold transition shadow-sm">
                                            Start Now
                                        </button>
                                    </div>
                                </div>
                            </div>

                        ) : (
                        /* ═══════════════════ CONSULTING VIEW ═══════════════════ */
                            <div className="grid gap-4 md:gap-6 lg:gap-8 lg:grid-cols-3">
                                <div className="lg:col-span-2 space-y-4 md:space-y-6 lg:space-y-8">
                                    {/* Score Cards */}
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
                                        {/* Goal Assessment */}
                                        <Card data-tour="goal" className="group p-5 md:p-6 bg-white rounded-2xl shadow-sm border border-slate-100 flex flex-col items-center text-center transition-shadow hover:shadow-md">
                                            <div className="flex items-center gap-2 mb-5">
                                                <span className="w-6 h-6 rounded-lg flex items-center justify-center" style={{ background: ACCENT_SOFT }}><Target className="w-3.5 h-3.5" style={{ color: ACCENT_ON_SOFT }} /></span>
                                                <h2 className="text-[15px] font-bold text-[#111827]">{t('dashboard.goalAssessment')}</h2>
                                            </div>
                                            <div className="relative w-36 h-36 md:w-40 md:h-40 mb-4">
                                                <svg className="w-full h-full transform -rotate-90" viewBox="0 0 160 160">
                                                    <circle cx="80" cy="80" r="70" stroke="#F3F4F6" strokeWidth="12" fill="none" />
                                                    <circle cx="80" cy="80" r="70" stroke="#FACC15" strokeWidth="12" fill="none"
                                                        strokeDasharray={`${2 * Math.PI * 70 * (goalInfo?.progress || 0.62)} ${2 * Math.PI * 70}`}
                                                        strokeLinecap="round" />
                                                </svg>
                                                <div className="absolute inset-0 flex items-center justify-center">
                                                    <span className="text-4xl font-extrabold text-[#111827]">{goalGroup}</span>
                                                </div>
                                            </div>
                                            <h3 className="text-[13px] font-bold mb-1.5 text-[#111827]">{goalInfo?.title || t('dashboard.capitalReadiness')}</h3>
                                            <p className="text-[11px] text-gray-500 leading-relaxed mb-5 px-1 min-h-[40px]">
                                                {goalInfo?.description || t('dashboard.capitalReadinessDesc')}
                                            </p>
                                            <button onClick={() => hasGoalAssessment ? setShowScoreSheet(true) : navigate('/readiness-journey')}
                                                className="mt-auto inline-flex items-center gap-1.5 px-5 py-2 bg-white border border-slate-200 rounded-xl text-gray-700 text-xs font-semibold hover:bg-gray-50 hover:border-slate-300 transition-colors">
                                                {hasGoalAssessment ? t('dashboard.viewMore') : t('dashboard.completeNow')}
                                            </button>
                                        </Card>

                                        {/* IR Score */}
                                        <Card data-tour="ir-score" className="group p-5 md:p-6 bg-white rounded-2xl shadow-sm border border-slate-100 flex flex-col items-center text-center transition-shadow hover:shadow-md">
                                            <div className="flex items-center gap-2 mb-5">
                                                <span className="w-6 h-6 rounded-lg flex items-center justify-center" style={{ background: '#EAF2FB' }}><BarChart3 className="w-3.5 h-3.5" style={{ color: '#1976D2' }} /></span>
                                                <h2 className="text-[15px] font-bold text-[#111827]">{t('dashboard.irScore')}</h2>
                                            </div>
                                            <div className="relative w-36 h-36 md:w-40 md:h-40 mb-4">
                                                <svg className="w-full h-full transform -rotate-90" viewBox="0 0 160 160">
                                                    <circle cx="80" cy="80" r="70" stroke="#F3F4F6" strokeWidth="12" fill="none" />
                                                    {hasIRScore && <circle cx="80" cy="80" r="70" stroke={irColor} strokeWidth="12" fill="none"
                                                        strokeDasharray={`${2 * Math.PI * 70 * irProgress} ${2 * Math.PI * 70}`} strokeLinecap="round" style={{ transition: 'stroke-dasharray 1s ease' }} />}
                                                </svg>
                                                <div className="absolute inset-0 flex flex-col items-center justify-center">
                                                    <span className="text-4xl font-extrabold" style={{ color: hasIRScore ? irColor : '#111827' }}>{hasIRScore ? irScore : '-'}</span>
                                                    {hasIRScore && <span className="text-[9px] font-semibold text-gray-400 mt-0.5">/ 100</span>}
                                                </div>
                                            </div>
                                            <div className="min-h-[54px] mb-4 px-1 flex flex-col items-center justify-start gap-2">
                                                {hasIRScore ? (
                                                    <>
                                                        <span className="inline-block px-3 py-1 rounded-full text-[11px] font-bold" style={{ backgroundColor: `${irColor}1A`, color: irColor }}>{irBandLabel}</span>
                                                        <p className="text-[10.5px] text-gray-500 leading-relaxed">{irDesc}</p>
                                                    </>
                                                ) : (
                                                    <p className="text-[10.5px] text-gray-500 leading-relaxed">{t('dashboard.completeToSee')}</p>
                                                )}
                                            </div>
                                            <button onClick={() => hasIRScore ? navigate('/ir-score') : navigate('/readiness-journey')}
                                                className="mt-auto inline-flex items-center gap-1.5 px-5 py-2 bg-white border border-slate-200 rounded-xl text-gray-700 text-xs font-semibold hover:bg-gray-50 hover:border-slate-300 transition-colors">
                                                {hasIRScore ? <>View report <ArrowRight className="w-3.5 h-3.5" /></> : t('dashboard.completeNow')}
                                            </button>
                                        </Card>

                                        {/* Capital Readiness Score — premium (paid report), consulting-blue */}
                                        <Card data-tour="capital"
                                            className="group relative overflow-hidden p-5 md:p-6 rounded-2xl border flex flex-col items-center text-center transition-all duration-300 hover:-translate-y-1"
                                            style={{
                                                borderColor: '#C7DCF5',
                                                background: 'linear-gradient(165deg, #EAF2FB 0%, #FFFFFF 58%)',
                                                boxShadow: '0 22px 48px -22px rgba(25,118,210,.55)',
                                            }}
                                        >
                                            {/* decorative glows */}
                                            <div className="absolute -top-12 -right-10 w-36 h-36 rounded-full pointer-events-none" style={{ background: 'radial-gradient(circle, rgba(25,118,210,.16), transparent 70%)' }} />
                                            <div className="absolute -bottom-14 -left-10 w-32 h-32 rounded-full pointer-events-none" style={{ background: 'radial-gradient(circle, rgba(23,38,58,.08), transparent 70%)' }} />
                                            {/* top gradient ribbon */}
                                            <div className="absolute top-0 inset-x-0 h-1.5" style={{ background: 'linear-gradient(90deg,#2E83E6,#1976D2,#17263A)' }} />
                                            {/* premium badge */}
                                            <div className="absolute top-3 right-3 inline-flex items-center gap-1 px-2.5 py-[3px] rounded-full text-[8px] font-extrabold uppercase tracking-wider shadow-sm" style={{ background: 'linear-gradient(120deg,#17263A,#1E3A5F)', color: '#FFD54F' }}>
                                                <Crown className="w-2.5 h-2.5" /> Premium
                                            </div>
                                            <div className="relative flex items-center gap-2 mb-5 mt-0.5">
                                                <span className="w-6 h-6 rounded-lg flex items-center justify-center" style={{ background: '#DCEBFB' }}><Sparkles className="w-3.5 h-3.5" style={{ color: '#1976D2' }} /></span>
                                                <h2 className="text-[15px] font-bold" style={{ color: '#17263A' }}>Capital Readiness</h2>
                                            </div>
                                            <div className="relative w-36 h-36 md:w-40 md:h-40 mb-4">
                                                <svg className="w-full h-full transform -rotate-90" viewBox="0 0 160 160">
                                                    <defs>
                                                        <linearGradient id="crRingBlue" x1="0" y1="0" x2="1" y2="1">
                                                            <stop offset="0" stopColor="#4CA1F0" /><stop offset="0.5" stopColor="#1976D2" /><stop offset="1" stopColor="#125AA6" />
                                                        </linearGradient>
                                                    </defs>
                                                    <circle cx="80" cy="80" r="70" stroke="#E4ECF6" strokeWidth="12" fill="none" />
                                                    {hasCrScore && (
                                                        <circle cx="80" cy="80" r="70" stroke="url(#crRingBlue)" strokeWidth="12" fill="none"
                                                            strokeDasharray={`${2 * Math.PI * 70 * crPct / 100} ${2 * Math.PI * 70}`}
                                                            strokeLinecap="round" style={{ filter: 'drop-shadow(0 0 7px rgba(25,118,210,.45))', transition: 'stroke-dasharray 1s ease' }} />
                                                    )}
                                                </svg>
                                                <div className="absolute inset-0 flex flex-col items-center justify-center">
                                                    <span className="text-[42px] font-black leading-none" style={{ color: '#17263A' }}>
                                                        {hasCrScore ? crScore.toFixed(1) : '-'}
                                                    </span>
                                                    {hasCrScore && <span className="text-[9px] font-semibold text-slate-400 mt-1">/ 100</span>}
                                                </div>
                                            </div>
                                            <div className="relative min-h-[54px] mb-4 px-1 flex flex-col items-center justify-start gap-2">
                                                {hasCrScore ? (
                                                    <>
                                                        <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-[11px] font-bold" style={{ backgroundColor: '#EAF2FB', color: '#125AA6' }}><Sparkles className="w-3 h-3" />{crBand}</span>
                                                        <p className="text-[10.5px] text-slate-500 leading-relaxed">{reportUnlocked ? 'Your full report is unlocked. View your breakdown.' : 'Unlock your full factor-by-factor report.'}</p>
                                                    </>
                                                ) : (
                                                    <p className="text-[10.5px] text-slate-500 leading-relaxed">Complete the 14-question assessment to get your score.</p>
                                                )}
                                            </div>
                                            <button
                                                onClick={() => navigate('/capital-assessment')}
                                                className="relative mt-auto inline-flex items-center gap-1.5 px-6 py-2.5 rounded-xl text-xs font-bold text-white shadow-md transition-all hover:shadow-lg hover:brightness-110"
                                                style={{ background: 'linear-gradient(120deg,#2E83E6,#1976D2 55%,#17263A)' }}
                                            >
                                                {hasCrScore ? <>See Breakdown <ArrowRight className="w-3.5 h-3.5" /></> : <>Take Assessment <ArrowRight className="w-3.5 h-3.5" /></>}
                                            </button>
                                        </Card>
                                    </div>

                                    {/* Profile Completion — consulting readiness progress */}
                                    <Card data-tour="profile" className="p-5 md:p-6 bg-white rounded-2xl shadow-sm border-none">
                                        <div className="flex items-center justify-between mb-3">
                                            <div>
                                                <h3 className="text-sm md:text-base font-bold text-[#111827]">Profile Completion</h3>
                                                <p className="text-[11px] text-gray-500 mt-0.5">Complete all steps to become fully investor-ready</p>
                                            </div>
                                            <span className="text-2xl md:text-3xl font-extrabold" style={{ color: profileCompletion === 100 ? '#15824E' : '#1D74F5' }}>{profileCompletion}%</span>
                                        </div>
                                        <div className="h-2.5 w-full bg-gray-100 rounded-full overflow-hidden mb-4">
                                            <div className="h-full rounded-full transition-all duration-700" style={{ width: `${profileCompletion}%`, background: profileCompletion === 100 ? 'linear-gradient(90deg,#10B981,#15824E)' : 'linear-gradient(90deg,#60A5FA,#1D74F5)' }} />
                                        </div>
                                        <div className="grid grid-cols-3 gap-2 md:gap-3">
                                            {consultingSteps.map(s => (
                                                <div key={s.key} className={`flex items-center gap-2 rounded-xl px-2.5 py-2 ${s.done ? 'bg-blue-50' : 'bg-gray-50'}`}>
                                                    <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold shrink-0 ${s.done ? 'bg-[#1D74F5] text-white' : 'bg-gray-200 text-gray-400'}`}>{s.done ? '✓' : '•'}</span>
                                                    <span className={`text-[10px] md:text-xs font-semibold leading-tight ${s.done ? 'text-[#111827]' : 'text-gray-400'}`}>{s.label}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </Card>

                                    {/* Same upload prompt in the other dashboard view; retires on
                                        the same signal so the two views cannot disagree. */}
                                    {hasUploadedDocs === false && <Card className="p-4 md:p-6 bg-white rounded-xl md:rounded-2xl shadow-sm border-none">
                                        <div className="flex items-center justify-between cursor-pointer" onClick={() => navigate('/data-room')}>
                                            <div className="flex items-center gap-4">
                                                <div className="h-10 w-10 rounded-full bg-gray-100 flex items-center justify-center">
                                                    <img src={cloud} alt="Upload" className="w-5 h-5 opacity-60" loading="lazy" />
                                                </div>
                                                <div>
                                                    <h3 className="text-sm md:text-base font-medium text-[#111827]">{t('dashboard.uploadDocuments')}</h3>
                                                    <p className="text-xs md:text-sm text-gray-400">{t('dashboard.uploadDocumentsDesc')}</p>
                                                </div>
                                            </div>
                                            <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                            </svg>
                                        </div>
                                    </Card>}

                                    <Card className="bg-[#D1E9FF] border-none p-6 md:p-8 rounded-2xl md:rounded-3xl relative overflow-hidden">
                                        <div className="relative z-10 max-w-lg">
                                            <div className="w-12 h-12 mb-6"><img src={speak} alt="" className="w-full h-full object-contain" loading="lazy" /></div>
                                            <h3 className="text-xl md:text-2xl font-bold text-[#111827] mb-3">{t('dashboard.speakConsultant')}</h3>
                                            <p className="text-sm md:text-base text-[#111827] mb-6 md:mb-8 leading-relaxed">{t('dashboard.speakConsultantDesc')}</p>
                                            <button onClick={() => navigate('/pricing2')} className="rounded-lg bg-[#1D74F5] px-8 py-3 text-sm font-semibold text-white hover:bg-blue-600 transition shadow-sm">
                                                {t('dashboard.upgrade')}
                                            </button>
                                        </div>
                                    </Card>

                                    <Card className="bg-[#FFF7E0] border-none p-8 rounded-3xl relative overflow-hidden">
                                        <div className="relative z-10 max-w-lg">
                                            <div className="w-12 h-12 mb-6"><img src={raise} alt="" className="w-full h-full object-contain" loading="lazy" /></div>
                                            <h3 className="text-2xl font-bold text-[#111827] mb-3">{t('dashboard.readyToRaise')}</h3>
                                            <p className="text-base text-[#111827] mb-8 leading-relaxed">{t('dashboard.readyToRaiseDesc')}</p>
                                            <button onClick={() => navigate('/raisedcap')} className="rounded-lg bg-[#FACC15] px-8 py-3 text-sm font-semibold text-[#111827] hover:bg-yellow-400 transition shadow-sm">
                                                {t('dashboard.startNow')}
                                            </button>
                                        </div>
                                    </Card>
                                </div>

                                <div className="space-y-4 md:space-y-6">
                                    {/* Capital report — paid unlock. Once purchased, the report is ready to
                                        download; before that, prompt the user to unlock it. */}
                                    {hasCrScore && reportUnlocked && (
                                        <Card
                                            className="border-none p-5 md:p-6 rounded-2xl md:rounded-3xl relative overflow-hidden"
                                            style={{ background: 'linear-gradient(135deg, #1976D2 0%, #17263A 100%)' }}
                                        >
                                            <div className="absolute -right-7 -top-7 w-24 h-24 rounded-full bg-white/10" />
                                            <div className="relative z-10">
                                                <div className="w-9 h-9 rounded-xl bg-white/20 flex items-center justify-center mb-3">
                                                    <FileText className="w-5 h-5 text-white" />
                                                </div>
                                                <span className="inline-block text-[10px] font-bold uppercase tracking-wider text-white/80 mb-1">Capital Readiness · {crScore.toFixed(1)}/100</span>
                                                <h3 className="text-lg md:text-xl font-extrabold text-white mb-1.5 leading-tight">Your report is ready</h3>
                                                <p className="text-xs text-white/90 mb-4 leading-relaxed">See your full capital readiness breakdown: your strengths and the gaps to close before you raise.</p>
                                                <button onClick={handleDownloadReport} disabled={reportBusy} className="w-full rounded-xl bg-white py-2.5 text-sm font-bold hover:bg-white/90 transition shadow-sm disabled:opacity-70" style={{ color: '#1976D2' }}>
                                                    {reportBusy ? 'Preparing your report…' : 'Check Your Report →'}
                                                </button>
                                            </div>
                                        </Card>
                                    )}
                                    {hasCrScore && !reportUnlocked && (
                                        <Card
                                            className="border-none p-5 md:p-6 rounded-2xl md:rounded-3xl relative overflow-hidden"
                                            style={{ background: 'linear-gradient(135deg, #1976D2 0%, #17263A 100%)' }}
                                        >
                                            <div className="absolute -right-7 -top-7 w-24 h-24 rounded-full bg-white/10" />
                                            <div className="relative z-10">
                                                <div className="w-9 h-9 rounded-xl bg-white/20 flex items-center justify-center mb-3">
                                                    <Lock className="w-5 h-5 text-white" />
                                                </div>
                                                <span className="inline-block text-[10px] font-bold uppercase tracking-wider text-white/80 mb-1">Capital Readiness · {crScore.toFixed(1)}/100</span>
                                                <h3 className="text-lg md:text-xl font-extrabold text-white mb-1.5 leading-tight">Unlock your full report</h3>
                                                <p className="text-xs text-white/90 mb-4 leading-relaxed">Your score is ready. Unlock the full breakdown: your strengths and the gaps to close before you raise.</p>
                                                <button onClick={() => navigate('/capital-assessment')} className="w-full rounded-xl bg-white py-2.5 text-sm font-bold hover:bg-white/90 transition shadow-sm" style={{ color: '#1976D2' }}>
                                                    Unlock report →
                                                </button>
                                            </div>
                                        </Card>
                                    )}

                                    {/* Notification Center — anchor the tour to the card, not its icon tile. */}
                                    <div data-tour="notifications" className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5">
                                        <div className="flex items-center gap-2.5 mb-5">
                                            <div className="w-7 h-7 rounded-lg bg-blue-50 flex items-center justify-center">
                                                <MessageSquare className="w-4 h-4 text-blue-500" />
                                            </div>
                                            <h3 className="text-sm font-bold text-gray-900">Notification Center </h3>
                                        </div>
                                        <div className="space-y-4">
                                            {criticalAlerts.length === 0 ? (
                                                <div className="flex flex-col items-center justify-center py-4 gap-1.5">
                                                    <MessageSquare className="w-7 h-7 text-gray-200" />
                                                    <p className="text-xs text-gray-400 text-center">No notifications yet</p>
                                                </div>
                                            ) : (
                                                criticalAlerts.map(({ Icon, iconBg, iconColor, title, description, action, actionFn }, i) => (
                                                    <div key={i} className="flex items-start gap-3">
                                                        <div className="w-8 h-8 rounded-lg shrink-0 flex items-center justify-center mt-0.5" style={{ backgroundColor: iconBg }}>
                                                            <Icon className="w-4 h-4" style={{ color: iconColor }} />
                                                        </div>
                                                        <div className="flex-1 min-w-0">
                                                            <p className="text-xs font-semibold text-gray-900 leading-snug">{title}</p>
                                                            {description && <p className="text-[11px] text-gray-500 mt-0.5 leading-snug">{description}</p>}
                                                            <button onClick={actionFn} className="text-[11px] font-bold text-blue-500 hover:text-blue-700 mt-1.5 transition">{action}</button>
                                                        </div>
                                                    </div>
                                                ))
                                            )}
                                        </div>
                                        <button onClick={() => navigate('/notifications')} className="mt-5 w-full text-center text-[11px] font-medium text-gray-400 hover:text-gray-600 transition">
                                            View all notifications
                                        </button>
                                    </div>

                                    <Card className="bg-[#D1E9FF] border-none p-4 md:p-6 rounded-2xl md:rounded-3xl h-auto">
                                        <div className="w-8 h-8 mb-4"><img src={cam} alt="camera" className="w-full h-full object-contain" loading="lazy" /></div>
                                        <h3 className="text-lg font-bold text-[#111827] mb-3">{t('dashboard.freeConsultation')}</h3>
                                        <p className="text-xs text-[#111827] mb-6 leading-relaxed">{t('dashboard.freeConsultationDesc')}</p>
                                        <button onClick={() => navigate('/book-consultation')} className="w-full rounded-lg bg-[#1D74F5] py-3 text-sm font-semibold text-white hover:bg-blue-600 transition shadow-sm">
                                            {t('dashboard.bookNow')}
                                        </button>
                                    </Card>
                                    <Card className="bg-[#FFF7E0] border-none p-4 md:p-6 rounded-2xl md:rounded-3xl h-auto">
                                        <div className="w-8 h-8 mb-4"><img src={cam} alt="" className="w-full h-full object-contain" loading="lazy" /></div>
                                        <h3 className="text-lg font-bold text-[#111827] mb-3">{t('dashboard.taxFilings')}</h3>
                                        <p className="text-xs text-[#111827] mb-6 leading-relaxed">{t('dashboard.taxFilingsDesc')}</p>
                                        <button className="w-full rounded-lg bg-[#FACC15] py-3 text-sm font-semibold text-[#111827] hover:bg-yellow-400 transition shadow-sm" onClick={() => navigate(requestPath('tax-filings'))}>
                                            {t('dashboard.bookNow')}
                                        </button>
                                    </Card>
                                    <Card className="bg-[#F3F4F6] border-none p-6 rounded-3xl h-auto">
                                        <div className="w-8 h-8 mb-4"><img src={leg} alt="" className="w-full h-full object-contain" loading="lazy" /></div>
                                        <h3 className="text-lg font-bold text-[#111827] mb-3">{t('dashboard.legalSupport')}</h3>
                                        <p className="text-xs text-[#111827] mb-6 leading-relaxed">{t('dashboard.legalSupportDesc')}</p>
                                        <button onClick={() => navigate(requestPath('corporate-services'))} className="w-full rounded-lg bg-[#D1D5DB] py-3 text-sm font-semibold text-[#111827] hover:bg-gray-400 transition shadow-sm">
                                            {t('dashboard.startNow')}
                                        </button>
                                    </Card>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div onClick={() => navigate('/data-room')} className="cursor-pointer">
                                            <Card className="p-6 bg-white rounded-2xl border-none shadow-sm flex flex-col items-center justify-center text-center hover:shadow-md transition-shadow h-32">
                                                <div className="w-6 h-6 mb-3"><img src={doc} alt="" className="w-full h-full object-contain" loading="lazy" /></div>
                                                <p className="text-xs font-medium text-[#111827]">{t('dashboard.documents')}</p>
                                            </Card>
                                        </div>
                                        <div onClick={() => navigate('/book-consultation')} className="cursor-pointer">
                                            <Card className="p-6 bg-white rounded-2xl border-none shadow-sm flex flex-col items-center justify-center text-center hover:shadow-md transition-shadow h-32">
                                                <div className="w-6 h-6 mb-3"><img src={cam} alt="" className="w-full h-full object-contain" loading="lazy" /></div>
                                                <p className="text-xs font-medium text-[#111827]">{t('dashboard.consultations')}</p>
                                            </Card>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </main>

            {/* First-run guided tour (auto after sign-up; ?tour=1 replays it) */}
            {showTour && (
                <OnboardingTour
                    steps={tourSteps}
                    accent={activeServiceView === 'accounting' ? ACCENT : '#1976D2'}
                    onClose={finishTour}
                />
            )}

            {/* Score Details Modal */}
            <div className={`fixed inset-0 z-50 ${showScoreSheet ? '' : 'pointer-events-none'}`}>
                <div onClick={() => setShowScoreSheet(false)}
                    className={`absolute inset-0 bg-black/50 transition-opacity duration-300 ${showScoreSheet ? 'opacity-100' : 'opacity-0'}`} />
                <div className={`fixed bottom-0 left-0 right-0 w-full h-[50vh] bg-white rounded-t-2xl shadow-2xl overflow-auto transition-transform duration-300 ${showScoreSheet ? 'translate-y-0' : 'translate-y-full'}`}>
                    <div className="p-8">
                        <div className="flex items-center justify-center relative mb-8">
                            <h3 className="text-xl font-semibold text-slate-900">{t('dashboard.scoreDetails')}</h3>
                            <button onClick={() => setShowScoreSheet(false)} className="absolute right-0 p-1 hover:bg-slate-100 rounded-full transition-colors">
                                <svg className="w-6 h-6 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                        <div className="flex flex-col lg:flex-row items-start lg:items-center gap-6 mb-8 pb-8 border-b border-slate-200">
                            <div className="shrink-0"><Logo className="h-10" to="/dashboard" /></div>
                            <div className="flex-1 grid grid-cols-2 lg:grid-cols-4 gap-8">
                                {[
                                    { label: t('dashboard.goalAssessment'), value: goalGroup },
                                    { label: t('dashboard.irScore'), value: hasIRScore ? irScore : '-' },
                                    { label: t('dashboard.totalScore'), value: hasIRScore ? `${t('dashboard.' + titleInfo.column)} ${t('dashboard.' + titleInfo.row)}` : '-' },
                                    { label: t('dashboard.profileTitle'), value: hasIRScore ? `${t('dashboard.' + titleInfo.column)} ${t('dashboard.' + titleInfo.row)}` : '-' },
                                ].map(({ label, value }) => (
                                    <div key={label}>
                                        <div className="text-xs text-slate-500 mb-1">{label}</div>
                                        <div className="text-lg font-bold text-slate-900">{value}</div>
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-8">
                            <div>
                                <h4 className="text-base font-semibold text-slate-900 mb-4">{t('dashboard.gapsBreakdown')}</h4>
                                <ul className="space-y-2 text-sm text-slate-700">
                                    {['team', 'market', 'model', 'traction', 'financial', 'pitch', 'compliance'].map(k => (
                                        <li key={k} className="flex items-start gap-2"><span className="text-slate-400 mt-1">•</span><span>{t(`dashboard.gaps.${k}`)}</span></li>
                                    ))}
                                </ul>
                            </div>
                            <div>
                                <h4 className="text-base font-semibold text-slate-900 mb-4">{t('dashboard.planBreakdown')}</h4>
                                <ul className="space-y-2 text-sm text-slate-700">
                                    {['team', 'market', 'model', 'mvp', 'budget', 'pitch', 'dataroom'].map(k => (
                                        <li key={k} className="flex items-start gap-2"><span className="text-slate-400 mt-1">•</span><span>{t(`dashboard.plan.${k}`)}</span></li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                        <div className="flex justify-end gap-3 pt-4 border-t border-slate-200">
                            <button onClick={() => setShowScoreSheet(false)} className="px-6 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition">
                                {t('dashboard.viewLess')}
                            </button>
                            <button onClick={() => navigate('/raisedcap')} className="btn-acc px-6 py-2 text-sm font-semibold rounded-lg transition">
                                {t('dashboard.select')}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
